/**
 * WP-7 interactive game: HTML chrome + the human-vs-AI turn loop, now opening on
 * a real buy/deploy screen (ui/buy.ts) instead of an auto-bought fleet. Red is
 * the player; Yellow is the fog-honest AI. Click a Red ship to select it (reach
 * buoys appear), click a buoy to move. The Build button (B) opens the shipyard
 * menu any time during your turn — buying is an instant points purchase
 * (ui/build.ts). F / End Turn ends the turn directly, running both fleets' move
 * + the single end-of-turn combat phase + end-of-turn economy.
 *
 * Adds over WP-6: the buy screen, the on-demand build menu, a win-ratio
 * prediction HUD line + animated VP tickers, a game-over stats screen, and
 * autosave/resume (ui/save.ts) so a reload restores the battle exactly.
 *
 * Combat is still resolved functionally and reported in the log — the cinematic
 * viewer is WP-8.
 */
import {
  aiBuyFleet, aiMoveAll, clampDifficulty, difficultyProfile, DEFAULT_DIFFICULTY,
  DIFFICULTY_NAMES, type AIProfile,
} from '../rules/ai';
import { hexDist } from '../rules/board';
import { toggleAnchored, canStop, executeMove, moveAllowance, pathTo, reachable, toggleSubmerged } from '../rules/movement';
import { visibleOnBoard } from '../rules/fog';
import { UNITS } from '../rules/ships';
import {
  cityAt, newGameState, shipsAt, type GameState, type MapSize, type Ship, type Side,
} from '../rules/state';
import { endOfTurn, resolveCombatSites, vpProjection } from '../rules/turn';
import type { CombatEvent, CombatSite } from '../rules/combat';
import { buildBoardScene, minimapSize, type BoardView } from '../render/board-gl';
import type { BoardDynamic } from '../render/geometry';
import { startBuyScreen, type BuyHandle } from './buy';
import { renderBuildYards } from './build';
import { startCombatViewer } from './combat-viewer';
import { startTutorial, type TutorialController } from './tutorial';
import type { TutorialTrigger } from '../rules/tutorial';
import { audio } from '../audio/engine';
import { clearSave, getSkipCinematic, loadGame, saveGame, type LogEntry } from './save';
import { SCREEN_MAX_W, SCREEN_MAX_H } from './screen';
import { showSettings, type SettingsHandle } from './settings';
import {
  ICON_ANCHOR, ICON_FLAG, ICON_DIVE, ICON_SURFACE, ICON_NEXT, ICON_MAP, ICON_GEAR,
  ICON_HAMMER, ICON_SWORDS, ICON_UNDO, ICON_REDO, ICON_X, ICON_VP,
} from './icons';

const CSS = `
.ar-game { --navy:hsl(217, 53.7%, 16.1%); --navy2:hsl(220, 60%, 11.8%); --ink:hsl(219, 58.3%, 9.4%); --parch:hsl(44, 53.7%, 83.9%); --parch2:hsl(40, 46.9%, 71.2%);
  --wood:hsl(31, 47.6%, 28.4%); --wood-d:hsl(30, 54.2%, 18.8%); --brass:hsl(43, 63.2%, 58.4%); --brass-l:hsl(46, 80.7%, 73.5%); --brass-d:hsl(42, 61.6%, 34.7%);
  --txt:hsl(28, 55.6%, 10.6%); --head:hsl(23, 66.7%, 21.2%); --redx:hsl(7, 70.6%, 41.4%); --redx-l:hsl(8, 100%, 78%); --yelx:hsl(44, 84.4%, 37.6%); --yelx-l:hsl(43, 100%, 71.6%);
  position: fixed; inset: 0; max-width: ${SCREEN_MAX_W}px; max-height: ${SCREEN_MAX_H}px; margin: auto;
  display: flex; flex-direction: column;
  font-family: Georgia, 'Times New Roman', serif; color: var(--txt); user-select: none; }
.ar-top { flex: 0 0 auto; display: flex; align-items: stretch; background: linear-gradient(180deg, hsl(219, 52.2%, 22.2%), var(--navy) 60%, var(--navy2));
  border: 2px solid var(--ink); border-bottom: 3px solid var(--brass-d); }

/* turn block: a pie/clock dial that fills clockwise across the battle, with the
   "Turn N / max" text to its right; flips to red on the final turn. The dial fill
   animates via the registered --p custom property (a number 0–100). */
@property --p { syntax: '<number>'; initial-value: 0; inherits: false; }
.ar-turn { display: flex; flex-direction: row; align-items: center; gap: 10px;
  padding: 6px 16px; border-right: 1px solid hsla(0, 0%, 100%, 0.08); }
.ar-turn-pie { position: relative; box-sizing: border-box; width: 26px; height: 26px; border-radius: 50%;
  flex: 0 0 auto; overflow: hidden; border: 2px solid var(--brass-d);
  background: hsla(0, 0%, 0%, 0.5); box-shadow: 0 1px 2px hsla(0, 0%, 0%, 0.5); }
/* the conic fill is a separate layer clipped to the round frame by the parent's
   overflow:hidden, so the wedge can never spill past the golden border. */
.ar-turn-pie-fill { position: absolute; inset: 0; transition: --p 0.45s ease;
  background: conic-gradient(var(--brass-l) calc(var(--p, 0) * 1%), transparent 0); }
.ar-turn-text { display: flex; flex-direction: column; line-height: 0.95; }
.ar-turn-cap { color: var(--brass-l); font-weight: 700; font-size: 11px; letter-spacing: 1px;
  text-transform: uppercase; text-shadow: 0 1px 0 hsl(0, 0%, 0%); }
.ar-turn-count { color: var(--brass-l); font-weight: 700; text-shadow: 0 1px 0 hsl(0, 0%, 0%); white-space: nowrap; }
.ar-turn-count b { font-size: 14px; }
.ar-turn-max { opacity: 0.7; font-size: 11px; margin-left: 2px; }

/* scoreboard: one proportional Red|Yellow bar with each side's VP printed inside
   its own region, flanked by side chips, with the win-line marker on top. */
.ar-score { display: flex; flex-direction: row; align-items: center; gap: 9px; flex: 1 1 auto;
  min-width: 280px; padding: 6px 16px; border-right: 1px solid hsla(0, 0%, 100%, 0.06); }
/* side chip: colour + an SVG shape (Red = O ring, Yellow = X cross) so the two
   read apart by shape as well as colour. Used in the scoreboard + win-text line. */
.ar-team-chip { width: 16px; height: 16px; border-radius: 3px; flex: 0 0 auto; border: 1px solid hsla(0, 0%, 0%, 0.55);
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.45), 0 1px 2px hsla(0, 0%, 0%, 0.4);
  display: inline-flex; align-items: center; justify-content: center; }
.ar-team-chip svg { width: 72%; height: 72%; display: block; }
.ar-team-chip.R { background: linear-gradient(180deg, hsl(7, 72.2%, 56.3%), var(--redx)); color: hsl(0, 0%, 100%); }
.ar-team-chip.Y { background: linear-gradient(180deg, hsl(44, 86.3%, 68.6%), var(--yelx)); color: hsl(42, 81.3%, 12.5%); }
.ar-gauge { position: relative; flex: 1 1 auto; min-width: 0; }
.ar-gauge-bar { position: relative; height: 24px; border-radius: 5px; overflow: hidden;
  background: hsla(0, 0%, 0%, 0.5); border: 1px solid hsla(0, 0%, 0%, 0.55); box-shadow: inset 0 1px 2px hsla(0, 0%, 0%, 0.55); }
.ar-gauge-R, .ar-gauge-Y { position: absolute; top: 0; bottom: 0; width: 50%; transition: width 0.5s ease; }
.ar-gauge-R { left: 0; background: linear-gradient(180deg, hsl(7, 72.2%, 56.3%), var(--redx)); }
.ar-gauge-Y { right: 0; background: linear-gradient(180deg, hsl(44, 86.3%, 68.6%), var(--yelx)); }
/* VP printed inside the bar, anchored to each end. The dark outline that keeps
   white legible on both bar halves is CRISP (0-blur hard offsets / SVG stroke) —
   blurred shadows fuzzed the glyphs. */
.ar-vp { position: absolute; top: 50%; transform: translateY(-50%); z-index: 2; color: hsl(0, 0%, 100%);
  display: inline-flex; align-items: center; gap: 1px;
  font-weight: 800; font-size: 14px; font-variant-numeric: tabular-nums; white-space: nowrap;
  text-shadow: 1px 0 0 hsla(0, 0%, 0%, 0.85), -1px 0 0 hsla(0, 0%, 0%, 0.85),
    0 1px 0 hsla(0, 0%, 0%, 0.85), 0 -1px 0 hsla(0, 0%, 0%, 0.85); }
.ar-vp.R { left: 9px; } .ar-vp.Y { right: 9px; }
.ar-vp b { font-weight: inherit; }
/* the currency star inherits the number's white; its outline is a real vector
   stroke painted behind the fill (stays sharp at any size, unlike drop-shadow). */
.ar-vp svg { width: 1.15em; height: 1.15em; display: block;
  stroke: hsla(0, 0%, 0%, 0.85); stroke-width: 3; stroke-linejoin: round; paint-order: stroke; }
/* win line: the VP-share the favored side must reach to clear the win ratio.
   An inverted black triangle sits inside the bar, flush with the top edge and
   pointing down; when the red/yellow boundary crosses it, the favored side has
   clinched. */
.ar-gauge-mark { position: absolute; top: 0; transform: translateX(-50%); z-index: 4;
  width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent;
  border-top: 9px solid hsl(0, 0%, 4.7%); filter: drop-shadow(0 1px 1px hsla(0, 0%, 100%, 0.5)); transition: left 0.45s ease; }

/* context heading (mode name) + sub-line (campaign state / instant battle /
   quick map size + enemy rank) between the score bar and the mute button. */
.ar-mode { flex: 0 0 auto; display: flex; flex-direction: column; justify-content: center; align-items: center;
  text-align: center; gap: 1px; max-width: 300px; padding: 0 18px; border-left: 1px solid hsla(0, 0%, 100%, 0.06); }
.ar-mode-name { color: var(--brass-l); font-weight: 700; font-size: 15px; letter-spacing: 0.3px;
  text-shadow: 0 1px 0 hsl(0, 0%, 0%); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ar-mode-sub { color: hsl(41, 37.7%, 67.3%); font-size: 11px; font-style: italic; letter-spacing: 0.2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ar-mode-sub:empty { display: none; }
/* enemy-difficulty picker — shown in the top bar DURING BUY, in place of the
   score gauge; both occupy the same flex-grow middle slot (only one at a time). */
.ar-topdiff { display: none; flex: 0 1 auto; min-width: 0; margin-right: auto; align-items: center; gap: 9px;
  padding: 6px 16px; }
.ar-topdiff label { flex: 0 0 auto; color: var(--brass-l); font-weight: 700; font-size: 11px; letter-spacing: 1px;
  text-transform: uppercase; text-shadow: 0 1px 0 hsl(0, 0%, 0%); }
.ar-topdiff select { flex: 0 1 auto; min-width: 0; max-width: 100%; font-family: inherit; font-size: 13px; padding: 5px 9px;
  border: 1px solid var(--ink); border-radius: 4px; background: hsl(200, 100%, 89%); color: var(--txt);
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.5); cursor: pointer; }
.ar-game.ar-buying .ar-score { display: none; }
.ar-game.ar-buying .ar-topdiff { display: flex; }
/* Campaign buy has no enemy picker nor score bar to occupy the emptied middle,
   so the turn dial takes over the score bar's flex-grow role and pushes the
   heading + gear to the right. The heading stays content-width (flex:0 0 auto)
   at every width — overriding the ≤740px "fill the row" rule below — so it
   right-aligns against the gear instead of stretching and centering. */
.ar-game.ar-m-campaign.ar-buying .ar-turn { margin-right: auto; }
.ar-game.ar-m-campaign.ar-buying .ar-mode { flex: 0 0 auto; }
/* tighten the picker label to just "Enemy" on narrow screens, then drop it
   entirely (select only) when even narrower */
@media (max-width: 640px) { .ar-topdiff .ce-choose { display: none; } }
@media (max-width: 420px) { .ar-topdiff label { display: none; } }
/* below 360px the picker drops to its own row beneath the turn dial + gear —
   with room again for the "Enemy" label (restored from the <420px hide). */
@media (max-width: 360px) {
  .ar-game.ar-buying .ar-topdiff { order: 9; flex-basis: 100%; margin-right: 0;
    border-top: 1px solid hsla(0, 0%, 100%, 0.08); }
  .ar-game.ar-buying .ar-topdiff label { display: block; }
}
/* under 260px even the picker row is too tight for the label — select only */
@media (max-width: 260px) {
  .ar-game.ar-buying .ar-topdiff label { display: none; }
}
/* undo/redo are meaningless before play — hide them during the buy phase */
.ar-game.ar-buying .ar-hist { display: none; }
.ar-mute { align-self: stretch; background: transparent; border: none; border-left: 1px solid hsla(0, 0%, 100%, 0.08);
  color: hsl(43, 100%, 84.5%); font-size: 17px; line-height: 1; cursor: pointer; padding: 0 16px; transition: filter 0.1s, background 0.1s; }
.ar-mute:hover { filter: brightness(1.18); background: hsla(0, 0%, 100%, 0.1); }
#ar-settings-btn { display: inline-flex; align-items: center; justify-content: center; }
#ar-settings-btn svg { width: 23px; height: 23px; display: block; }
/* Undo/Redo in the top bar — proper raised buttons (a bordered, rounded, tinted
   fill) rather than flat mute-style glyphs, so they read as clickable; they dim
   when there's nothing to do. Overrides the .ar-mute stretch/borderless defaults. */
.ar-hist { align-self: center; display: inline-flex; align-items: center; justify-content: center;
  margin: 0 3px; padding: 5px 9px; border-radius: 5px;
  border: 1px solid hsla(43, 55%, 62%, 0.45); background: hsla(0, 0%, 100%, 0.07);
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.12); }
.ar-hist:hover:not(:disabled) { filter: none; background: hsla(0, 0%, 100%, 0.16); }
.ar-hist:active:not(:disabled) { transform: translateY(1px); box-shadow: inset 0 1px 3px hsla(0, 0%, 0%, 0.35); }
.ar-hist svg { width: 20px; height: 20px; display: block; }
.ar-hist:disabled { opacity: 0.3; cursor: default; filter: none; background: hsla(0, 0%, 100%, 0.04); box-shadow: none; }

/* normal centered text flow (NOT flex) so the line wraps as a paragraph and the
   separator can't get orphaned the way a flex item did. An opaque grey-blue strip
   in the canvas-backdrop hue (#41505c ≈ hsl(206,17%,31%)): a darker shade at the
   edges deepening to very dark under the centred text — no border/inset chrome. */
.ar-pred { flex: 0 0 auto; display: block; line-height: 1.5;
  background: linear-gradient(90deg, hsl(206, 17%, 17%), hsl(206, 17%, 8%) 50%, hsl(206, 17%, 17%));
  color: hsl(212, 62.7%, 88.4%); font-size: 11px; text-align: center; padding: 4px 14px; letter-spacing: 0.4px; }
.ar-pred b.win { color: hsl(131, 76.4%, 75.1%); } .ar-pred b.lose { color: var(--redx-l); }
.ar-pred .ar-team-chip { width: 13px; height: 13px; border-radius: 2px; vertical-align: middle; margin: 0 1px; }
.ar-pred .ar-sep { color: var(--brass-d); margin: 0 3px; }
.ar-board { flex: 1 1 auto; min-height: 0; position: relative; border: 2px solid var(--ink); background: hsl(219, 58.3%, 9.4%); overflow: hidden; }
/* kill the inline descender gap under the board canvas without zeroing
   line-height for text overlays (tutorial / orders / game-over) layered on top.
   touch-action:none lets drag-to-pan own touch gestures (no page scroll/zoom). */
.ar-board > canvas { display: block; touch-action: none; }
/* the bar can wrap: ship-card hugs its text (≤300), the log keeps a sensible
   floor, and the buttons drop to their own full-width row before the log ever
   gets crushed at tight widths. */
/* No min-height: the bar is only as tall as its content needs (a short bar when the
   log is near-empty), so it never reserves dead space below the controls. */
.ar-bottom { flex: 0 0 auto; display: flex; background:
  linear-gradient(180deg, hsl(45, 57.1%, 86.3%), var(--parch) 30%, var(--parch2));
  border: 2px solid var(--wood); border-top: 3px solid var(--brass-d); }
/* during the buy phase the play-phase log/selected-ship/controls bar has nothing
   to show — the buy menu (ui/buy.ts) takes the bottom-bar slot instead. */
.ar-game.ar-buying .ar-bottom { display: none; }
.ar-panel { padding: 9px 13px; }
/* on wide screens the right wrapper is transparent so log | sel | controls stay
   three flex columns; the narrow layout turns it into a real right column. */
.ar-right { display: contents; }
/* fixed-width selected-info column so the bar doesn't reflow as the text changes.
   It carries BOTH dividers (left + right) because, unlike the height-capped log, it
   stretches to the full bar height — so the dividers run all the way down. */
.ar-sel { flex: 0 0 150px; border-left: 2px solid var(--wood); border-right: 2px solid var(--wood);
  box-shadow: inset 1px 0 0 hsla(0, 0%, 100%, 0.35), inset -1px 0 0 hsla(0, 0%, 100%, 0.35); font-size: 13px; }
.ar-sel b { color: var(--head); font-size: 14px; }
/* small inline "deselect" (✕) shown in the selected-ship line when a ship is
   selected — a quick way to clear the selection besides clicking open sea. */
.ar-desel { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; padding: 0;
  margin-left: 6px; vertical-align: -3px; cursor: pointer; border: 1px solid var(--wood-d); border-radius: 3px;
  background: var(--brass); color: var(--txt); }
.ar-desel:hover { filter: brightness(1.08); }
.ar-desel svg { width: 10px; height: 10px; display: block; }
/* the move log is a wrapping grid of move "chips" (newest last). It is
   height-capped and scrolls on every width once it overflows, with a clearly
   visible brass scrollbar; new moves auto-scroll it to the bottom (renderLog). */
.ar-log { flex: 999 1 240px; min-width: 240px; font-size: 13px;
  display: flex; flex-wrap: wrap; gap: 5px; align-content: flex-start;
  /* Cap the log just under the controls' natural height (2 button rows + padding ≈
     105px, so ≤ ~84px content here) so the CONTROLS drive the bar height, not the
     log: the bar stays a constant height and extra moves scroll instead of growing
     it. (The ≤900 layout floats the log absolutely, so this cap only bites here.) */
  overflow: hidden auto; max-height: 84px; line-height: 1.4;
  scrollbar-width: thin; scrollbar-color: var(--brass-d) hsla(0, 0%, 0%, 0.18); }
.ar-log::-webkit-scrollbar { width: 11px; height: 9px; }
.ar-log::-webkit-scrollbar-track { background: hsla(0, 0%, 0%, 0.18); border-radius: 6px; }
.ar-log::-webkit-scrollbar-thumb { background: var(--brass-d); border-radius: 6px;
  border: 2px solid transparent; background-clip: content-box; }
.ar-log::-webkit-scrollbar-thumb:hover { background: var(--brass); }
/* Each log item is itself a mini flex row: icons/team-chips/coloured names and
   text runs all centre on one axis — no inline baseline games (vertical-align
   never quite lined the 12px chip up with the 12px text). The gap stands in
   for the single spaces the flex layout swallows between elements. */
.ar-logitem { align-self: flex-start; display: inline-flex; align-items: center; gap: 4px;
  white-space: nowrap; font-size: 12px; padding: 1px 7px;
  border-radius: 4px; background: hsla(0, 0%, 100%, 0.4); border: 1px solid hsla(31, 47.6%, 28.4%, 0.4);
  box-shadow: 0 1px 1px hsla(0, 0%, 0%, 0.08); }
/* current-turn move chips are clickable to replay their trail on the board; a
   solid left accent marks them, the selected chip goes white to match its trail. */
.ar-logitem--move { cursor: pointer; padding-left: 5px; border-left: 3px solid var(--brass-d);
  transition: background 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease; }
.ar-logitem--move:hover { background: hsla(0, 0%, 100%, 0.75); border-color: var(--wood); }
/* selected: a bright white fill + glow (no visible border) */
.ar-logitem--sel, .ar-logitem--sel:hover { background: hsl(0, 0%, 100%); border-color: hsl(0, 0%, 100%);
  border-left-style: solid; font-weight: 700; box-shadow: 0 0 0 1px hsl(0, 0%, 100%), 0 0 7px hsla(0, 0%, 100%, 0.85); }
.ar-log .R { color: var(--redx); font-weight: 700; } .ar-log .Y { color: var(--yelx); font-weight: 700; }
/* team chip sized down to sit inline in a log chip (e.g. "◉ took Boston");
   the flex logitem centres it — no vertical-align needed. The colour re-asserts
   the glyph ink: the .ar-log .R/.Y TEXT tints above match these selectors'
   specificity and would otherwise paint the O/X in the side colour itself —
   invisible on the chip's own background. */
.ar-log .ar-team-chip { width: 12px; height: 12px; }
.ar-log .ar-team-chip.R { color: hsl(0, 0%, 100%); }
.ar-log .ar-team-chip.Y { color: hsl(42, 81.3%, 12.5%); }
.ar-log-empty { align-self: center; color: hsl(31, 47.6%, 28.4%); font-style: italic; font-size: 13px; }
/* every action is an on-screen button now; they pack two-per-row and wrap as the
   panel narrows (End Turn ⁄ Orders on one line, Anchor ⁄ Submerge on the next). */
/* auto-fitting grid: pack two buttons per row whenever two ~118px columns fit,
   else collapse to a single full-width column. Because every column is an equal
   fraction, a wider label (e.g. "Anchored") can't bump one button to its own row
   the way wrap-on-flex did — it's either a clean 2×2 or a clean 1-per-row stack. */
.ar-controls { flex: 1 1 300px; display: grid; gap: 7px; align-content: center;
  /* at most 2 columns: each track must be ≥ half the width (50% − half-gap), so a
     3rd never fits; the 118px floor still lets it drop to 1 column when too narrow. */
  grid-template-columns: repeat(auto-fit, minmax(max(118px, 50% - 4px), 1fr)); }
.ar-controls .ar-btn { min-width: 0; display: flex; align-items: center; justify-content: center; gap: 6px; }
.ar-controls .ar-btn .ico { display: inline-flex; flex: 0 0 auto; }
.ar-controls .ar-btn .ico svg { display: block; width: 17px; height: 17px; }
.ar-btn { font-family: inherit; font-weight: 700; font-size: 15px; padding: 9px 10px; cursor: pointer; color: var(--txt);
  background: linear-gradient(180deg, var(--brass-l), var(--brass) 55%, var(--brass-d));
  border: 2px solid var(--wood-d); border-radius: 4px;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.55), inset 0 -2px 2px hsla(0, 0%, 0%, 0.18), 0 1px 2px hsla(0, 0%, 0%, 0.3);
  text-shadow: 0 1px 0 hsla(0, 0%, 100%, 0.35); transition: filter 0.08s; }
.ar-btn:hover { filter: brightness(1.06); }
.ar-btn:active { box-shadow: inset 0 2px 4px hsla(0, 0%, 0%, 0.35); transform: translateY(1px); }
.ar-btn.primary { color: hsl(90, 100%, 95.7%); background: linear-gradient(180deg, hsl(105, 47.8%, 64.7%), hsl(105, 38.4%, 43.9%) 55%, hsl(105, 41.7%, 29.6%));
  border-color: hsl(110, 40.6%, 25.1%); text-shadow: 0 1px 0 hsla(0, 0%, 0%, 0.3); }
.ar-btn:disabled { opacity: 0.45; cursor: default; filter: none; box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.3); }
.ar-over { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: radial-gradient(ellipse at center, hsla(219, 51.8%, 16.3%, 0.86), hsla(220, 60%, 3.9%, 0.94)); color: hsl(45, 100%, 90.6%); text-align: center; z-index: 30; line-height: 1.45; }
.ar-over h1 { font-size: 40px; margin: 0 0 6px; letter-spacing: 4px; text-shadow: 0 2px 6px hsla(0, 0%, 0%, 0.6); }
.ar-over h1.win { color: hsl(131, 76.4%, 75.1%); } .ar-over h1.lose { color: var(--redx-l); }
.ar-over .sub { font-size: 15px; margin-bottom: 14px; color: hsl(42, 54.6%, 81%); }
.ar-over .ar-btn .ic { display: inline-flex; vertical-align: -0.18em; }
.ar-over .ar-btn .ic svg { width: 1.05em; height: 1.05em; }
.ar-over table { border-collapse: collapse; margin: 0 auto 16px; font-size: 14px;
  background: hsla(0, 0%, 0%, 0.22); border: 1px solid var(--brass-d); border-radius: 4px; overflow: hidden; }
.ar-over td { padding: 5px 16px; border-bottom: 1px solid hsla(45, 100%, 90.6%, 0.14); text-align: right; }
.ar-over tr:last-child td { border-bottom: none; }
.ar-over td.l { text-align: left; color: hsl(42, 42.6%, 73.3%); }
.ar-over td.R { color: var(--redx-l); font-weight: 700; } .ar-over td.Y { color: var(--yelx-l); font-weight: 700; }
/* header row: each side's team chip (colour + O/X) centred over its column, in
   place of the old "Red"/"Yellow" words. */
.ar-over td.hd { text-align: center; padding-top: 7px; padding-bottom: 3px; }
.ar-over td.hd .ar-team-chip { width: 18px; height: 18px; }
/* team chips read inline in the result sub-line + card headers */
.ar-over .sub .ar-team-chip { vertical-align: -3px; margin: 0 1px; }
/* End-turn confirmation: a centred card over a dimmed board listing this turn's orders. */
.ar-confirm { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: hsla(220, 60%, 4%, 0.62); z-index: 40; }
.ar-confirm-card { width: min(440px, 86%); max-height: 80%; display: flex; flex-direction: column; text-align: center;
  padding: 18px 20px; border: 2px solid var(--brass-d); border-radius: 8px; box-shadow: 0 12px 44px hsla(0, 0%, 0%, 0.55);
  background: linear-gradient(180deg, hsl(219, 52.2%, 22.2%), var(--navy2)); color: hsl(45, 100%, 90.6%); }
.ar-confirm-card h2 { margin: 0 0 9px; font-size: 21px; letter-spacing: 1px; }
.ar-confirm-sub { font-size: 12.5px; font-style: italic; color: hsl(42, 54.6%, 81%); margin-bottom: 12px; }
/* this turn's orders as a compact wrapping grid of chips — the same style as the
   move log — so the list stays short and scrolls instead of growing the window. */
.ar-confirm-list { display: flex; flex-wrap: wrap; gap: 5px; align-content: flex-start; justify-content: center;
  margin: 0 0 14px; padding: 8px 10px; overflow-y: auto; min-height: 0;
  background: hsla(0, 0%, 0%, 0.24); border: 1px solid var(--wood-d); border-radius: 4px;
  scrollbar-width: thin; scrollbar-color: var(--brass-d) hsla(0, 0%, 0%, 0.18); }
.ar-confirm-list::-webkit-scrollbar { width: 10px; }
.ar-confirm-list::-webkit-scrollbar-track { background: hsla(0, 0%, 0%, 0.24); border-radius: 6px; }
.ar-confirm-list::-webkit-scrollbar-thumb { background: var(--brass-d); border-radius: 6px; border: 2px solid transparent; background-clip: content-box; }
.ar-confirm-list .chip { align-self: flex-start; white-space: nowrap; font-size: 12px; padding: 2px 8px;
  border-radius: 4px; background: hsla(45, 100%, 90.6%, 0.1); border: 1px solid hsla(45, 100%, 90.6%, 0.22); color: hsl(45, 100%, 90.6%); }
.ar-confirm-list .chip svg { width: 1em; height: 1em; vertical-align: -0.15em; }
.ar-confirm-none { font-size: 13px; font-style: italic; color: hsl(42, 54.6%, 81%); margin: 4px 0 16px; }
.ar-confirm-btns { display: flex; gap: 10px; justify-content: center; }
.ar-confirm-btns .ar-btn { min-width: 132px; }
/* Fleet/orders window (.ar-owin) — a floating panel on the LEFT of the board,
   shared by the buy-screen "Fleet orders" and the in-play "Ship orders" list. Its
   header mirrors the settings window: a red close button on the left, a beveled
   divider, then the heading (icon + title) on a navy bar. Below it, a scrolling
   body of ship rows. */
/* width caps to the board width on narrow screens so it never overflows the
   viewport (min() with the available width, like the settings panel's max-width). */
.ar-owin { position: absolute; top: 6px; left: 6px; width: min(312px, calc(100% - 12px)); max-height: calc(100% - 12px);
  box-sizing: border-box; /* include the 3px border in width/height so the 6px margin stays even on all sides */
  display: flex; flex-direction: column; overflow: hidden; z-index: 22;
  border: 3px solid var(--wood); border-radius: 6px; box-shadow: 0 6px 22px hsla(0, 0%, 0%, 0.5);
  font-family: Georgia, serif; --hue: 208; } /* accent hue: Fleet = sky blue (buy chip +/− hue); Build overrides to orange */
/* header wraps like the settings header: if the close button + heading can't share
   a line (very narrow window) the heading drops to its own line instead of clipping. */
.ar-owin .hd { flex: 0 0 auto; display: flex; flex-wrap: wrap; align-items: stretch;
  background: linear-gradient(180deg, hsl(var(--hue), 66%, 58%), hsl(var(--hue), 70%, 47%)); border-bottom: 2px solid hsl(var(--hue), 55%, 30%); }
/* red close button on the left, same chrome as the settings close button */
.ar-owin .hd .cls { --h:4; font-family: inherit; font-weight: 700; cursor: pointer; color: #fff;
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 11px; font-size: 13px;
  background: linear-gradient(180deg, hsl(var(--h),62%,55%), hsl(var(--h),62%,47%) 55%, hsl(var(--h),62%,39%));
  border: none; border-right: 1px solid hsla(0, 0%, 0%, 0.35);
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.4), inset 0 -2px 2px hsla(0, 0%, 0%, 0.22); }
.ar-owin .hd .cls:hover { filter: brightness(1.08); }
.ar-owin .hd .cls:active { transform: translateY(1px); }
.ar-owin .hd .cls svg { width: 12px; height: 12px; display: block; }
.ar-owin .hd h3 { flex: 1 1 auto; min-width: 0; margin: 0; display: flex; align-items: center; gap: 7px; padding: 6px 12px;
  color: #fff; text-shadow: 0 1px 1px hsla(0, 0%, 0%, 0.35); font-size: 15px; letter-spacing: 0.5px;
  box-shadow: inset 1px 0 0 hsla(0, 0%, 100%, 0.18); }
.ar-owin .hd h3 svg { flex: 0 0 auto; }
.ar-owin .hd h3 svg { width: 16px; height: 16px; display: block; }
.ar-owin .body { flex: 1 1 auto; min-height: 0; overflow-y: auto; font-size: 13px; padding: 8px 11px;
  background: linear-gradient(180deg, hsl(44, 55.6%, 89.4%), var(--parch) 30%, var(--parch2)); }
.ar-owin .hint { margin: 0 0 6px; font-size: 10px; font-style: italic; color: var(--wood-d); line-height: 1.3; }
/* rows: name + course + clear. On a narrow window the course text shrinks and
   wraps (min-width:0) instead of forcing the window wider; a very tight row wraps
   the course onto its own line (flex-wrap). */
.ar-owin .tf { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; padding: 5px 5px; cursor: pointer; border-radius: 4px;
  border-bottom: 1px solid hsla(31, 47.6%, 28.4%, 0.18); }
.ar-owin .tf:hover { background: hsla(31, 47.6%, 28.4%, 0.1); }
.ar-owin .tf.on { background: hsla(124, 44.4%, 33.1%, 0.16); box-shadow: inset 0 0 0 1.5px hsl(124, 40%, 40%); }
.ar-owin .tf .nm { flex: 0 1 auto; min-width: 0; font-weight: 700; color: var(--head); overflow-wrap: anywhere; }
.ar-owin .tf .crs { flex: 1 1 auto; min-width: 0; text-align: right; font-size: 12px; overflow-wrap: anywhere; }
.ar-owin .tf .dest { color: var(--head); font-weight: 700; }
.ar-owin .tf .arr { color: hsl(132, 55.1%, 27.1%); font-weight: 700; }
.ar-owin .tf .none { font-style: italic; color: hsl(31, 47.6%, 28.4%); }
/* the +/× row buttons: set/edit a target (+) and cancel it (×) — a matched brass
   pair, mirroring the −/+ steppers on the buy chips. The + turns green while its
   row is the one being edited (dropdowns open). */
.ar-owin .tf .obtn { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px;
  padding: 0; cursor: pointer; border: 1px solid var(--wood-d); border-radius: 3px; background: var(--brass); color: var(--txt); }
.ar-owin .tf .obtn:hover { filter: brightness(1.06); }
.ar-owin .tf .obtn svg { width: 11px; height: 11px; display: block; }
.ar-owin .tf .add.on { background: linear-gradient(180deg, hsl(124, 40%, 52%), hsl(124, 44%, 40%)); border-color: hsl(124, 44%, 28%); color: #fff; }
.ar-owin .body > .none { font-style: italic; color: hsl(31, 47.6%, 28.4%); }
/* Expanded row: the port picker(s) drop onto their own full-width line under the
   ship name. On the buy screen it's From → To with a bare arrow between (no
   labels — the dropdowns are self-explanatory); in play it's a single picker.
   The dropdowns share the line and shrink (min-width:0) rather than wrap. */
.ar-owin .tf .legs { flex: 1 1 100%; display: flex; flex-wrap: nowrap; gap: 4px 8px; align-items: center; margin-top: 2px; }
.ar-owin .tf .legs select { flex: 1 1 60px; }
/* extra right padding leaves room for the native dropdown arrow so the option
   text never runs under it; long names truncate with an ellipsis instead. */
.ar-owin select { flex: 1 1 auto; font-family: inherit; font-size: 12px; padding: 2px 20px 2px 6px; max-width: none; min-width: 0;
  border: 1px solid var(--wood-d); border-radius: 3px; background: hsl(200, 100%, 92%); color: var(--txt); cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* Buy-screen Fleet window: every ship already has a home port, so both pickers are
   always shown (no +/× toggle). Rows are a fixed-column TABLE so name / From / → / To
   line up across every row. The name column is sized for the common names; a long
   one ("Armed Transport") truncates with an ellipsis rather than widening every row.
   Then two equal-width dropdown columns with the arrow centred between them. */
#ar-fleet-win { width: min(392px, calc(100% - 20px)); }
#ar-fleet-win .tf { display: grid; grid-template-columns: 84px minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center; column-gap: 8px; }
#ar-fleet-win .tf .nm { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* course arrow between the pickers: a shape (not a text glyph), in the same blue as
   the buy +/− stepper buttons. */
#ar-fleet-win .tf .arr { display: inline-flex; align-items: center; justify-content: center; color: hsl(208, 70%, 47%); }
#ar-fleet-win .tf .arr svg { width: 16px; height: 16px; display: block; }
/* Narrow screens: the single-line table crushes the dropdowns. Below ~380px the
   From/To pickers stack (name across the top), kept close together, with the course
   arrow in a slim LEFT column spanning both rows — pointing down, dot near From,
   head near To — like the map's course trail. */
@media (max-width: 380px) {
  #ar-fleet-win .tf { grid-template-columns: 8px minmax(0, 1fr);
    grid-template-areas: "nm nm" "arr from" "arr to"; row-gap: 6px; column-gap: 4px; }
  #ar-fleet-win .tf .nm { grid-area: nm; }
  #ar-fleet-win .tf select.from { grid-area: from; }
  #ar-fleet-win .tf select.to { grid-area: to; }
  #ar-fleet-win .tf .arr { grid-area: arr; align-self: center; justify-self: center; margin-left: -3px; transform: rotate(90deg); }
}
/* In-play Ship-orders window: like the buy window, but a single destination picker
   (ships are already placed, so there's no home port). It only needs name + one
   dropdown, so it's narrower than the buy window and stacks only when genuinely
   cramped (below ~300px, vs 380px for the two-dropdown buy window). */
#ar-orders-panel { width: min(264px, calc(100% - 20px)); }
#ar-orders-panel .tf { display: grid; grid-template-columns: 84px minmax(0, 1fr);
  align-items: center; column-gap: 8px; }
#ar-orders-panel .tf .nm { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
@media (max-width: 300px) {
  #ar-orders-panel .tf { grid-template-columns: minmax(0, 1fr); row-gap: 3px; }
}
/* reopen tab shown where the window was when it is closed (buy screen). */
.ar-owin-reopen { --hue: 208; position: absolute; top: 6px; left: 6px; z-index: 22; display: inline-flex; align-items: center; gap: 6px;
  font-family: Georgia, serif; font-weight: 700; font-size: 13px; cursor: pointer; color: #fff; padding: 8px 12px;
  background: linear-gradient(180deg, hsl(var(--hue), 66%, 58%), hsl(var(--hue), 70%, 47%)); border: 2px solid hsl(var(--hue), 55%, 30%); border-radius: 6px;
  box-shadow: 0 3px 12px hsla(0, 0%, 0%, 0.45); text-shadow: 0 1px 1px hsla(0, 0%, 0%, 0.35); }
/* Build accent = orange; Fleet keeps the default sky blue. Same shading, only hue. */
#ar-build-reopen, #ar-build-panel { --hue: 30; }
/* Build options are single-column now, so the window doesn't need the full width. */
#ar-build-panel { width: min(264px, calc(100% - 12px)); }
.ar-owin-reopen:hover { filter: brightness(1.1); }
.ar-owin-reopen svg { width: 15px; height: 15px; display: block; }
/* In-play: the Fleet + Build reopen tabs sit side by side in this container at the
   top-left, so the tabs flow instead of each anchoring to the same absolute spot. */
.ar-owin-tabs { position: absolute; top: 6px; left: 6px; z-index: 22; display: flex; gap: 6px; }
.ar-owin-tabs .ar-owin-reopen { position: static; }
/* Orders toolbar button in its active (mode-on) state — a green "engaged" look. */
.ar-controls .ar-btn.on { background: linear-gradient(180deg, hsl(124, 40%, 52%), hsl(124, 44%, 40%));
  border-color: hsl(124, 44%, 28%); color: #fff; }
.ar-pop { position: absolute; transform: translate(-50%, -50%); z-index: 24; pointer-events: none;
  font-family: Georgia, serif; font-weight: 700; font-size: 17px; white-space: nowrap;
  text-shadow: 0 1px 2px hsla(0, 0%, 0%, 0.85), 0 0 3px hsla(0, 0%, 0%, 0.6); animation: ar-pop-rise 1.25s ease-out forwards; }
.ar-pop.R { color: hsl(8, 100%, 78%); } .ar-pop.Y { color: hsl(43, 100%, 71.6%); } .ar-pop.x { color: hsl(0, 0%, 100%); }
.ar-zoom { position: absolute; right: 6px; z-index: 18; display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
/* +/− stack vertically; the minimap toggle (left) and the zoom read-out (right)
 * share a row beneath them. */
.ar-zoom-row { display: flex; flex-direction: row; align-items: center; gap: 6px; }
.ar-zoom button { width: 31px; height: 31px; box-sizing: border-box; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: linear-gradient(180deg, hsla(210, 10%, 60%, 0.72), hsla(210, 9%, 46%, 0.72) 60%, hsla(210, 9%, 38%, 0.72));
  border: 2px solid hsl(210, 10%, 70%); border-radius: 6px; }
.ar-zoom button svg { width: 16px; height: 16px; display: block; }
.ar-zoom button svg path, .ar-zoom button svg rect { fill: #fff; stroke: #000; stroke-width: 0.6; stroke-linejoin: round; }
.ar-zoom button:hover:not(:disabled) { filter: brightness(1.25); }
.ar-zoom button:active:not(:disabled) { transform: translateY(1px); }
/* the readout is itself a (cycle-zoom) button: identical chrome to the +/− buttons,
   just round instead of rounded-rect, with the level text on top. */
.ar-zoom .ar-zoom-lvl { border-radius: 50%; padding: 0; font-family: inherit;
  font-size: 10.5px; font-weight: 700; font-variant-numeric: tabular-nums; letter-spacing: 0.2px;
  color: hsl(220, 13%, 84%); text-shadow: 0 1px 2px hsla(0, 0%, 0%, 0.55); }
.ar-zoom button:disabled { opacity: 0.22; cursor: default; }
/* Minimap toggle: same chrome as the zoom buttons, but a white outline glyph. */
/* Minimap toggle: the 🗺 emoji (like the title Campaign button), with a diagonal
 * slash overlaid in the hidden state instead of any disabled-style dimming. */
.ar-zoom .ar-mini-toggle { position: relative; }
.ar-zoom .ar-mini-toggle .ic { display: inline-flex; line-height: 1; }
/* filled white glyph with a thin black border, like the +/− buttons (the generic
   .ar-zoom button svg rule supplies fill:#fff; stroke:#000). */
.ar-zoom .ar-mini-toggle .ic svg { width: 18px; height: 18px; display: block; }
.ar-zoom .ar-mini-toggle.is-off::after {
  content: ''; position: absolute; left: 50%; top: 50%; width: 23px; height: 2.4px;
  background: #fff; border-radius: 2px; transform: translate(-50%, -50%) rotate(-45deg);
  box-shadow: 0 0 0 1.3px hsla(0, 0%, 0%, 0.6); }
@keyframes ar-pop-rise {
  0% { opacity: 0; transform: translate(-50%, -35%) scale(0.7); }
  18% { opacity: 1; transform: translate(-50%, -55%) scale(1.05); }
  100% { opacity: 0; transform: translate(-50%, -130%) scale(1); }
}

/* ---- responsive ----------------------------------------------------------
   The rules above are the ≥tablet design — also the size the golden
   screenshots are captured at (1300×760), so they stay untouched. */

/* Two-column bottom bar, kicking in as soon as three columns would crowd (no
   awkward in-between wrap): the log fills the left and scrolls; the right column
   stacks selected-info over the buttons and, being the only in-flow child, sets
   the bar height — so the log never stretches it. */
@media (max-width: 900px) {
  .ar-bottom { display: block; position: relative; }
  .ar-right { width: 264px; margin-left: auto; display: flex; flex-direction: column; }
  .ar-log { position: absolute; inset: 0 264px 0 0; min-width: 0; max-height: none; overflow-y: auto;
    border-right: 2px solid var(--wood); box-shadow: none; padding: 4px 11px; font-size: 12px; }
  /* reset the wide-layout flex: in this vertical column its 300px basis would
     become a tall HEIGHT — here both boxes just hug their content. */
  .ar-sel { flex: 0 0 auto; max-width: none; border-left: none; border-right: none; box-shadow: none; padding: 6px 11px; font-size: 12px; }
  .ar-controls { flex: 0 0 auto; gap: 7px; padding: 7px 9px; }
  /* keep labels on one line; the grid (above) holds the 2×2 shape. Trim the
     side padding so even the widest label ("Anchored") fits its half-row cell. */
  .ar-controls .ar-btn { white-space: nowrap; padding-left: 6px; padding-right: 6px; }
}

/* Phone chrome: wrap the top bar, shrink the strips. (The zoom/minimap buttons keep
   their fixed size at every width — they don't grow on narrow viewports.) */
@media (max-width: 699px) {
  .ar-turn { padding: 5px 11px; min-width: 0; }
  .ar-score { min-width: 0; padding: 5px 11px; }
  .ar-mute { padding: 0 12px; }
  /* keep the win-info font-size constant across widths (only trim its padding on
     narrow screens) — a text-size jump at the breakpoint read as a glitch. */
  .ar-pred { padding: 4px 9px; }
}

/* Below this width the single row gets cramped (the turn dial + undo/redo + score
   + heading + gear no longer fit), so the score bar drops to its own full-width
   row; the turn dial · undo/redo · heading · gear stay on row 1. Bumped up from
   480px to make room for the undo/redo buttons now sitting next to the turn dial
   (a single row needs ~710px before the score gauge's 280px min-width overflows). */
@media (max-width: 740px) {
  .ar-top { flex-wrap: wrap; }
  /* heading fills row 1, centering its text; flex-basis 0 + min-width 0 so it
     SHRINKS (text truncates) rather than forcing the gear onto its own line. */
  .ar-mode { flex: 1 1 0; min-width: 0; max-width: none; }
  .ar-score { order: 5; flex: 1 1 100%; border-right: none; border-top: 1px solid hsla(0, 0%, 100%, 0.08); }
}

/* Narrower still: no room for the heading beside the turn dial + undo/redo + gear,
   so hide it. (Bumped up from 280px for the same reason.) */
@media (max-width: 400px) {
  /* keep the buy-screen heading down to a much narrower width (hidden at ≤280px
     below); only the play HUD drops it here. */
  .ar-game:not(.ar-buying) .ar-mode { display: none; }
  .ar-hist { padding: 5px 7px; }
  /* only the gear floats to the right — undo/redo stay beside the turn dial. */
  #ar-settings-btn { margin-left: auto; }
}

/* The buy heading persists far longer than the play HUD's — only drop it when the
   top bar is genuinely too tight (≤280px). */
@media (max-width: 280px) {
  .ar-game.ar-buying .ar-mode { display: none; }
}

/* very narrow: no room for the log beside the controls — move it on top as a
   single-line, horizontally-scrollable strip; info + buttons take the full
   width below it. */
@media (max-width: 439px) {
  .ar-log { position: static; flex-wrap: nowrap; overflow: auto hidden; max-height: none;
    border-right: none; border-bottom: 2px solid var(--wood); }
  .ar-right { width: auto; margin-left: 0; }
}

/* Drop the win-info (prediction) strip at the same short-height breakpoint where
   the controls collapse to icon-only — it's the least essential HUD line when
   vertical space is tight. It's the shared game HUD, so this hides it on the
   buy/deploy screen too. */
@media (max-height: 600px) {
  .ar-pred { display: none; }
}
/* short viewports (landscape phones): keep the bottom bar shallow so the board
   keeps the height. */
@media (max-height: 480px) {
  .ar-bottom { min-height: 0; }
  .ar-sel, .ar-log { padding-top: 5px; padding-bottom: 5px; }
  .ar-controls { gap: 6px; }
  .ar-controls .ar-btn { padding: 7px 8px; font-size: 13px; }
}

/* Collapse the four controls to icon-only in a single row when either there isn't
   width for the 2×2 labelled grid (narrow portrait) OR the viewport is short
   (< 600px tall), where the labelled buttons would eat too much vertical space. */
@media (max-width: 440px) and (max-height: 1000px), (max-height: 600px) {
  .ar-controls { grid-template-columns: repeat(4, 1fr); }
  .ar-controls .ar-btn { padding-left: 4px; padding-right: 4px; }
  .ar-controls .ar-btn .lbl { display: none; }
  .ar-controls .ar-btn .ico svg { width: 22px; height: 22px; }
}
/* short + wide (the in-flow 3-column bar): cap the log to ≈ the single icon-row's
   height so the controls drive a short bar AND the log fills it (stretches to that
   height) and scrolls — rather than leaving empty space. At ≤900px the log is
   absolutely positioned and fills its inset area on its own, so it's left uncapped. */
@media (min-width: 901px) and (max-height: 600px) {
  .ar-log { max-height: 44px; }
}
/* even narrower: let the four square up until they'd go narrower than tall, then
   fall back to two per row (≈ the width where each icon button becomes square). */
@media (max-width: 220px) and (max-height: 1000px) {
  .ar-controls { grid-template-columns: repeat(2, 1fr); }
}
`;

// Side glyphs drawn as SVG (inherit the chip's colour via currentColor):
// Red = O (ring), Yellow = X (cross).
const SHAPE_O = '<svg viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="3.4" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
const SHAPE_X = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3.2 3.2 L8.8 8.8 M8.8 3.2 L3.2 8.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const sideShape = (s: Side): string => (s === 'R' ? SHAPE_O : SHAPE_X);
// A team's identity chip (colour + O/X shape) — used everywhere a side is named,
// in place of the words "Red"/"Yellow" (scoreboard, prediction, log, game-over).
const teamChip = (s: Side): string => `<i class="ar-team-chip ${s}">${sideShape(s)}</i>`;

// Zoom stepper glyphs as filled SVG shapes (rendered white with a black border via
// CSS), instead of text +/− — matches the buy-screen chip steppers.
const ZOOM_MINUS = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="6.7" width="10" height="2.6" rx="1.3"/></svg>';
const ZOOM_PLUS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.7 3 H9.3 V6.7 H13 V9.3 H9.3 V13 H6.7 V9.3 H3 V6.7 H6.7 Z"/></svg>';
// Minimap toggle glyph: the shared map SVG (same as the title's Campaign button). The
// hidden state adds an `.is-off` slash over it (a "no" mark), like a mute button.
const MINIMAP_ICON = `<span class="ic">${ICON_MAP}</span>`;

const BOARD_W = 960;
const BOARD_H = 560;

// How many recent log entries to keep. Higher than before since the log now
// lays out as a wrapping grid of chips, so it uses width well and scrolls.
const LOG_MAX = 24;

// After the enemy admiral moves, hold this long on the post-move / pre-combat board
// so the player sees the fleet in position (esp. ships that closed into clear water
// beside them) before the combat cinematic — instead of jumping straight to battle.
const ENEMY_MOVE_REVEAL_MS = 1100;

// Map zoom: 1× frames the whole board (the default fit); higher zooms in. Driven
// by the bottom-right +/− buttons and the +/− keys.
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.1; // additive step, so the level reads in clean 0.1 increments

/** quick battle (default), a "Save the Admiral" state battle, the tutorial, or
 *  an instant battle (Jutland / Wolfpack — pre-built, no buy phase). */
export type GameMode = 'quick' | 'campaign' | 'tutorial' | 'instant';

export interface PlayOpts {
  size?: MapSize | undefined;
  seed?: number | undefined;
  /** restore an autosaved battle if one exists (default true; quick mode only) */
  resume?: boolean | undefined;
  /** quick (default), campaign, tutorial, or instant */
  mode?: GameMode | undefined;
  /** which side the player controls (default Red); instant battles can be Yellow */
  human?: Side | undefined;
  /** context heading shown in the top bar; falls back to a mode name
   *  ("Quick Battle", etc.). */
  label?: string | undefined;
  /** smaller sub-line under the heading (e.g. the campaign state / instant
   *  battle name). Quick battles derive their own (map size + enemy rank). */
  subLabel?: string | undefined;
  /** AI difficulty 1–11 (quick/instant). Default DEFAULT_DIFFICULTY (6). */
  difficulty?: number | undefined;
  /** pre-built battle state (campaign: budgets/ratio set + Yellow bought;
   *  tutorial: the hand-built map + both fleets). Skips newGameState/map-gen. */
  state?: GameState | undefined;
  /** campaign only: called at game over to record the result; returns the
   *  campaign summary line to show on the game-over card. */
  onResult?: ((youWon: boolean, g: GameState) => string) | undefined;
  /** campaign: return to the campaign map. tutorial: leave the lesson. */
  onExit?: (() => void) | undefined;
}

export type Phase = 'buy' | 'play' | 'over';

export interface PlayHandle {
  readonly g: GameState;
  view: BoardView;
  readonly phase: Phase;
  readonly mode: GameMode;
  /** buy-screen handle while in the buy phase, else null */
  readonly buy: BuyHandle | null;
  /** tutorial controller while in tutorial mode, else null */
  readonly tutorial: TutorialController | null;
  endTurn(): void;
  /** select the first Red ship that can move; returns its hex or null */
  selectFirstMovable(): { id: number; c: number; r: number } | null;
  /** a legal stopping tile for the current selection, or null */
  firstReachStop(): [number, number] | null;
  clickHex(c: number, r: number): void;
  selectedId: number | null;
}

export async function startGame(root: HTMLElement, opts: PlayOpts = {}): Promise<PlayHandle> {
  if (!document.getElementById('ar-style')) {
    const style = document.createElement('style');
    style.id = 'ar-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const human: Side = opts.human ?? 'R';
  const aiSide: Side = human === 'R' ? 'Y' : 'R';
  const mode: GameMode = opts.mode ?? 'quick';
  // AI difficulty (1–11). Mutable so the buy screen's picker can re-tune it
  // before the enemy fleet is bought; aiLevel mirrors it for the heading sub-line.
  let aiLevel = clampDifficulty(opts.difficulty ?? DEFAULT_DIFFICULTY);
  let aiProfile: AIProfile = difficultyProfile(aiLevel);
  // Only quick battles autosave/resume from the shared slot; campaign + tutorial
  // battles are launched fresh from their own screens.
  const restored = mode === 'quick' && opts.resume !== false ? loadGame() : null;
  const g = restored
    ? restored.g
    : (opts.state ?? newGameState({ size: opts.size ?? 'medium', seed: opts.seed ?? 7 }));
  // Quick mode: the enemy buys its fleet when play starts (after the difficulty
  // picker is settled). Campaign/tutorial/instant pass a pre-built state.
  const needsAiBuy = mode === 'quick' && !restored && !opts.state;

  // ---- chrome ----
  const wrap = document.createElement('div');
  // mode-scoped class so the buy-phase top-bar rules can differ per mode: quick's
  // enemy picker owns the middle, whereas campaign buy has no picker and instead
  // floats the heading + gear to the right (like the play-phase score bar does).
  wrap.className = `ar-game ar-m-${mode}`;
  // Enemy-difficulty picker for the top bar — shown DURING BUY only (quick mode),
  // in place of the score gauge (which isn't meaningful until the enemy fleet is
  // bought). Retuning it re-tunes the AI before it buys.
  const topDiffHtml = mode === 'quick'
    ? `<div class="ar-topdiff" id="ar-topdiff"><label for="ar-top-diff"><span class="ce-choose">Choose </span>Enemy</label><select id="ar-top-diff">`
      + DIFFICULTY_NAMES.map((n, i) => `<option value="${i + 1}"${i + 1 === aiLevel ? ' selected' : ''}>Lv ${i + 1} — ${n}</option>`).join('')
      + `</select></div>`
    : '';
  wrap.innerHTML = `
    <div class="ar-top">
      <div class="ar-turn" id="ar-turn">
        <div class="ar-turn-pie"><i class="ar-turn-pie-fill" id="ar-turn-pie"></i></div>
        <div class="ar-turn-text">
          <span class="ar-turn-cap">Turn</span>
          <span class="ar-turn-count"><b id="ar-turn-n">1</b><span class="ar-turn-max" id="ar-turn-max">/ 15</span></span>
        </div>
      </div>
      <button class="ar-mute ar-hist" id="ar-undo" title="Undo (Ctrl+Z)" disabled>${ICON_UNDO}</button>
      <button class="ar-mute ar-hist" id="ar-redo" title="Redo (Ctrl+Y)" disabled>${ICON_REDO}</button>
      <div class="ar-score">
        <i class="ar-team-chip R">${SHAPE_O}</i>
        <div class="ar-gauge">
          <div class="ar-gauge-bar">
            <i class="ar-gauge-R" id="ar-gauge-R"></i>
            <i class="ar-gauge-Y" id="ar-gauge-Y"></i>
            <span class="ar-vp R">${ICON_VP}<b id="ar-vpR">0</b></span>
            <span class="ar-vp Y">${ICON_VP}<b id="ar-vpY">0</b></span>
            <i class="ar-gauge-mark" id="ar-gauge-mark"></i>
          </div>
        </div>
        <i class="ar-team-chip Y">${SHAPE_X}</i>
      </div>
      ${topDiffHtml}
      <div class="ar-mode" id="ar-mode">
        <span class="ar-mode-name" id="ar-mode-name"></span>
        <span class="ar-mode-sub" id="ar-mode-sub"></span>
      </div>
      <button class="ar-mute" id="ar-settings-btn" title="Settings">${ICON_GEAR}</button>
    </div>
    <div class="ar-pred" id="ar-pred"></div>
    <div class="ar-board" id="ar-board"></div>
    <div class="ar-bottom">
      <div class="ar-panel ar-log" id="ar-log"></div>
      <div class="ar-right">
        <div class="ar-panel ar-sel" id="ar-sel">No ship selected.</div>
        <div class="ar-panel ar-controls">
          <button class="ar-btn" id="ar-unselect" disabled><span class="ico">${ICON_X}</span><span class="lbl">Unselect</span></button>
          <button class="ar-btn" id="ar-anchor" disabled><span class="ico">${ICON_ANCHOR}</span><span class="lbl">Anchor</span></button>
          <button class="ar-btn" id="ar-submerge" disabled><span class="ico">${ICON_DIVE}</span><span class="lbl">Submerge</span></button>
          <button class="ar-btn primary" id="ar-end" disabled><span class="lbl">End Turn</span><span class="ico">${ICON_NEXT}</span></button>
        </div>
      </div>
    </div>`;
  root.appendChild(wrap);
  const boardEl = wrap.querySelector<HTMLElement>('#ar-board')!;
  const $ = (id: string): HTMLElement => wrap.querySelector(`#${id}`)!;

  // context heading + sub-line between the score bar and the mute button. The
  // heading is the mode name (or a caller override); the sub is the campaign
  // state / instant battle, or — for quick battles — map size + enemy rank.
  const MODE_LABEL: Record<GameMode, string> = {
    quick: 'Quick Battle', campaign: 'Campaign', tutorial: 'Tutorial', instant: 'Instant Battle',
  };
  function updateModeSub(): void {
    if (mode === 'quick') {
      const size = opts.size ?? 'medium';
      $('ar-mode-sub').textContent = `${size[0]!.toUpperCase()}${size.slice(1)}, ${DIFFICULTY_NAMES[aiLevel - 1]}`;
    } else {
      $('ar-mode-sub').textContent = opts.subLabel ?? '';
    }
  }
  $('ar-mode-name').textContent = opts.label ?? MODE_LABEL[mode];
  updateModeSub();

  // Top-bar enemy-difficulty picker (quick mode): retuning it before the enemy
  // buys re-tunes the AI and refreshes the heading sub-line.
  if (mode === 'quick') {
    wrap.querySelector<HTMLSelectElement>('#ar-top-diff')?.addEventListener('change', (ev) => {
      aiLevel = clampDifficulty(Number((ev.target as HTMLSelectElement).value));
      aiProfile = difficultyProfile(aiLevel);
      updateModeSub();
    });
  }

  const view = await buildBoardScene(boardEl, g, {
    width: BOARD_W, height: BOARD_H, viewSide: human, animate: true, responsive: true,
    maxZoom: ZOOM_MAX,
  });

  // ---- interaction state ----
  let phase: Phase = 'buy';
  let buyHandle: BuyHandle | null = null;
  let selectedId: number | null = restored?.selectedId ?? null;
  let reachSet = new Set<string>();
  let reachList: [number, number, boolean][] = [];
  let cineActive = false;
  let confirmOpen = false; // the end-turn confirmation modal is up
  let ordersMode = false; // when on, board clicks set courses instead of moving ships
  let buildOpen = false; // the shipyard Build window is open
  let zoomLevel = ZOOM_MIN;
  let suppressClick = false; // swallow the click that ends a drag-pan
  let tut: TutorialController | null = null;
  const log: LogEntry[] = restored?.log ?? [];
  // index into `log` of the currently-selected current-turn move (whose dotted
  // trail is drawn), or -1. Recomputed to the last move after undo/redo/turn-end.
  let selMove = -1;

  const tutEvent = (ev: TutorialTrigger): void => tut?.event(ev);
  const shownVp: Record<Side, number> = { R: -1, Y: -1 };

  const sel = (): Ship | undefined => g.ships.find((s) => s.id === selectedId);

  // The advisory text strip was removed from the top bar (events show in the move
  // log; tips were optional flavour). Kept as a safe no-op so callers stay simple.
  function advisory(msg: string): void {
    const el = wrap.querySelector('#ar-adv');
    if (el) el.innerHTML = msg; // may carry the currency-star icon; msg is game-generated
  }

  // Rolling advisory flavour — a rotating tip appended at the start of each turn
  // (deterministic order, no RNG, so nothing here perturbs a replay).
  const TIPS = [
    'Transports capture cities — screen them with adjacent warships.',
    'Carriers lend +1 firepower to friends in adjacent hexes.',
    'Submerged subs are near-invisible — only sub-hunters (DD/PT/CR) can hurt them.',
    'One ship per tile — mass your fleet so several hit one enemy at once.',
    `A captured enemy home port scores double ${ICON_VP}.`,
    'Forts cannot move, but they hit hard on the coast.',
    'Anchor (D) a ship to hold a chokepoint and repair in port.',
    'Watch the prediction line — you must out-score by the ratio.',
    'Ships trade fire with every enemy in an adjacent tile, end of turn.',
    'Press F to end your turn once your fleet has moved.',
  ];
  let tipIdx = 0;
  const nextTip = (): string => TIPS[tipIdx++ % TIPS.length]!;

  /** Float a transient number/word above a hex (VP gains, captures). DOM overlay
   *  on the board; suppressed under automation so E2E/goldens are unaffected. */
  function floatPopup(c: number, r: number, text: string, cls: 'R' | 'Y' | 'x'): void {
    if (navigator.webdriver) return;
    const [sx, sy] = view.hexToScreen(c, r);
    const rect = boardEl.getBoundingClientRect();
    const pop = document.createElement('div');
    pop.className = `ar-pop ${cls}`;
    pop.innerHTML = text; // may carry the currency-star icon (${ICON_VP}); text is game-generated
    pop.style.left = `${sx - rect.left}px`;
    pop.style.top = `${sy - rect.top}px`;
    boardEl.appendChild(pop);
    window.setTimeout(() => pop.remove(), 1300);
  }

  /** Size the board canvas drawing buffer to the (flex-filled) board element. The
   *  chrome is a fixed full-viewport flex column, so the board grows to fill the gap
   *  between the sticky header and footer; this keeps the canvas pixel-crisp. */
  function resizeBoard(): void {
    view.resize(boardEl.clientWidth, boardEl.clientHeight);
  }

  /** Drag anywhere on open sea to pan the camera (responsive board). A drag is
   *  distinguished from a click by a small movement threshold; after a real drag
   *  the trailing click is swallowed so it doesn't select/move/assign. */
  function wirePan(): void {
    const canvas = view.renderer.domElement;
    let panning = false;
    let moved = false;
    let lastX = 0;
    let lastY = 0;
    canvas.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0) return;
      panning = true;
      moved = false;
      lastX = ev.clientX;
      lastY = ev.clientY;
    });
    window.addEventListener('pointermove', (ev) => {
      if (!panning) return;
      const dx = ev.clientX - lastX;
      const dy = ev.clientY - lastY;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      moved = true;
      lastX = ev.clientX;
      lastY = ev.clientY;
      view.panPixels(dx, dy);
    });
    window.addEventListener('pointerup', () => {
      if (panning && moved) suppressClick = true;
      panning = false;
    });
    // capture phase: drop the click that ends a drag before it reaches the
    // board's click handlers (clickHex / buy destination assignment)
    canvas.addEventListener('click', (ev) => {
      if (suppressClick) {
        suppressClick = false;
        ev.stopImmediatePropagation();
        ev.preventDefault();
      }
    }, true);
  }

  /** A current-turn move entry is selectable (its trail can be shown). */
  function isLiveMove(e: LogEntry): boolean {
    return !!e.move && e.move.turn === g.turn;
  }
  function renderLog(): void {
    const el = $('ar-log');
    el.innerHTML = log.length
      ? log
          .map((e, i) => {
            if (isLiveMove(e)) {
              const cls = `ar-logitem ar-logitem--move${i === selMove ? ' ar-logitem--sel' : ''}`;
              return `<span class="${cls}" data-mv="${i}" title="Show this move's path">${e.html}</span>`;
            }
            return `<span class="ar-logitem">${e.html}</span>`;
          })
          .join('')
      : '<span class="ar-log-empty">No moves made.</span>';
    el.querySelectorAll<HTMLElement>('[data-mv]').forEach((sp) => {
      sp.addEventListener('click', () => selectMove(Number(sp.dataset['mv'])));
    });
    // keep the newest move in view — bottom when stacked, far right when the
    // strip is a single horizontal row (very narrow widths).
    el.scrollTop = el.scrollHeight;
    el.scrollLeft = el.scrollWidth;
  }
  /** Pick move #i to visualise: highlight its log chip + draw its dotted trail. */
  function selectMove(i: number): void {
    if (!log[i] || !isLiveMove(log[i]!)) return;
    selMove = i;
    renderLog();
    refreshBoard();
  }
  /** After undo/redo/turn-end, re-select the LAST current-turn move (or none) and
   *  repaint the log so last turn's chips lose their selectable styling at once
   *  (not only when the next move is made). */
  function reselectLastMove(): void {
    selMove = -1;
    for (let i = log.length - 1; i >= 0; i--) {
      if (isLiveMove(log[i]!)) { selMove = i; break; }
    }
    renderLog();
  }
  /** The selected move's traversed path, if it's still a current-turn move. */
  function selectedTrail(): [number, number][] | undefined {
    const e = log[selMove];
    return e && isLiveMove(e) ? e.move!.path : undefined;
  }
  function pushLog(html: string): void {
    log.push({ html });
    if (log.length > LOG_MAX) { log.shift(); if (selMove >= 0) selMove--; }
    renderLog();
  }
  /** Log a ship move + record its traversed path; auto-selects it (the newest
   *  move is always the default selection). */
  function pushMove(html: string, path: [number, number][]): void {
    log.push({ html, move: { turn: g.turn, path } });
    if (log.length > LOG_MAX) { log.shift(); if (selMove >= 0) selMove--; }
    selMove = log.length - 1;
    renderLog();
  }
  /** Log a build, tagged with the turn so the end-turn confirmation can list it. */
  function pushBuild(html: string): void {
    log.push({ html, build: { turn: g.turn } });
    if (log.length > LOG_MAX) { log.shift(); if (selMove >= 0) selMove--; }
    renderLog();
  }

  /** Animate a VP number from its shown value up to the real total (VP only
   *  ever grows, so this is a monotonic count-up). */
  function tickVp(side: Side): void {
    const el = $(`ar-vp${side}`);
    const to = g.vp[side];
    let from = shownVp[side] < 0 ? to : shownVp[side];
    if (from >= to) {
      el.textContent = `${to}`;
      shownVp[side] = to;
      return;
    }
    const step = (): void => {
      from = Math.min(to, from + Math.max(1, Math.ceil((to - from) / 6)));
      el.textContent = `${from}`;
      shownVp[side] = from;
      if (from < to) requestAnimationFrame(step);
    };
    step();
  }

  /** Size the standing gauge to each side's share of total VP (50/50 before any
   *  is scored). Snaps via the CSS width transition. */
  function refreshGauge(): void {
    const tot = g.vp.R + g.vp.Y;
    const rPct = tot > 0 ? (g.vp.R / tot) * 100 : 50;
    $('ar-gauge-R').style.width = `${rPct}%`;
    $('ar-gauge-Y').style.width = `${100 - rPct}%`;
    // win line: the red-share at which the favored side exactly clears the win
    // ratio (favored vp = other vp × (1 + ratio/100)). Fixed per battle.
    const r = g.ratio / 100;
    const threshRed = g.favored === 'R' ? (1 + r) / (2 + r) : 1 / (2 + r);
    const mark = $('ar-gauge-mark');
    mark.style.left = `${threshRed * 100}%`;
    mark.title = `Win line — the favoured side must out-score by ${g.ratio}% to win`;
  }

  function refreshPrediction(): void {
    const p = vpProjection(g);
    const el = $('ar-pred');
    // each side is shown by its scoreboard chip (colour + O/X shape), not text.
    const chip = teamChip;
    // During buy the battle hasn't started (the human's VP is just their unspent
    // budget, the enemy's is 0), so a live "winning" projection is meaningless —
    // state the win condition instead: which side is favoured and by how much.
    if (phase === 'buy' || (g.vp.R === 0 && g.vp.Y === 0)) {
      el.innerHTML = `${chip(p.favored)} must lead by <b>+${g.ratio}%</b> to win`;
      return;
    }
    const cls = p.projectedWinner === human ? 'win' : 'lose';
    // "win line" = the ▼ marker on the gauge: how far the favored side is past /
    // short of the lead it needs to clinch.
    const tip = p.overWin >= 0
      ? `<b class="${cls}">+${p.overWin}%</b> past the win line`
      : `${chip(p.favored)} <b class="${cls}">${Math.abs(p.overWin)}%</b> short of the win line`;
    el.innerHTML = `${chip(p.projectedWinner)} winning&nbsp;<span class="ar-sep">—</span> ${tip}`;
  }

  function refreshHud(): void {
    // g.turn hits maxTurn+1 as the terminal "game over" marker — never show it;
    // clamp the displayed turn (and the dial fill) to maxTurn. The buy phase is
    // "turn 0" — the fleet isn't deployed yet, so no turn has begun.
    const shownTurn = phase === 'buy' ? 0 : Math.min(g.turn, g.maxTurn);
    $('ar-turn-n').textContent = `${shownTurn}`;
    $('ar-turn-max').textContent = `/ ${g.maxTurn}`;
    $('ar-turn-pie').style.setProperty('--p', `${Math.round((shownTurn / g.maxTurn) * 100)}`);
    tickVp('R');
    tickVp('Y');
    refreshGauge();
    refreshPrediction();
    const s = sel();
    if (s) {
      const mp = moveAllowance(s) - s.moved;
      let destLine = '';
      if (s.dest) {
        const city = cityAt(g, s.dest[0], s.dest[1]);
        const arrived = s.c === s.dest[0] && s.r === s.dest[1];
        destLine = arrived
          ? ` · ${ICON_FLAG} arrived at ${city?.name ?? 'destination'}`
          : ` · ${ICON_FLAG} orders: ${city?.name ?? `${s.dest[0]},${s.dest[1]}`} (${hexDist(s.c, s.r, s.dest[0], s.dest[1])} hexes)`;
      }
      // one flowing line — wraps naturally when the panel is narrow, stays single
      // line when there's room (no hard-coded <br> breaks).
      $('ar-sel').innerHTML = `<b>${UNITS[s.type].name}</b> (HP ${s.hp}/${UNITS[s.type].hp}) · `
        + `at ${s.c},${s.r} · move ${mp} left${s.done ? ' · done' : ''}${s.anchored ? ` · ${ICON_ANCHOR}` : ''}`
        + `${s.type === 'SS' ? ` · ${s.submerged ? 'submerged' : 'surfaced'}` : ''}${destLine}`;
    } else {
      $('ar-sel').textContent = phase === 'buy' ? 'Buying fleet…' : 'No ship selected.';
    }
    refreshControls();
  }

  function autosave(): void {
    // Only quick battles persist to the shared slot (campaign/tutorial are
    // launched fresh from their screens; campaign progress saves separately).
    if (mode === 'quick' && phase === 'play') saveGame(g, selectedId, log, aiLevel, opts.size ?? 'medium');
  }

  /** Dotted "follow the dots" trails from each of the player's ships to its
   *  standing-order destination (skipping ships already there). A planning aid —
   *  the rules never move a ship on its own. */
  // Unselected ships' order lines (drawn dimmed); the selected ship's is drawn
  // prominent via selectedOrderRoute() so it stands out without hiding the rest.
  function humanOrderRoutes(): [number, number][][] {
    return g.ships
      .filter((s) => s.side === human && s.dest && s.id !== selectedId && !(s.c === s.dest[0] && s.r === s.dest[1]))
      .map((s) => [[s.c, s.r], [s.dest![0], s.dest![1]]] as [number, number][]);
  }
  function selectedOrderRoute(): [number, number][] | undefined {
    const s = g.ships.find((x) => x.id === selectedId && x.side === human);
    if (!s || !s.dest || (s.c === s.dest[0] && s.r === s.dest[1])) return undefined;
    return [[s.c, s.r], [s.dest[0], s.dest[1]]];
  }

  /** Single choke-point for redrawing the board's dynamic layer during play:
   *  update() replaces the whole dynamic each call, so the current selection,
   *  reach buoys and order trails must all be re-supplied together every time. */
  function refreshBoard(extra: BoardDynamic = {}): void {
    // Fog-of-war only during play; the deploy screen shows the whole map (no ships
    // yet, and you need to see every port to plan courses).
    view.update({ selectedId: selectedId ?? undefined, reach: reachList, routes: humanOrderRoutes(), routeSel: selectedOrderRoute(), trail: selectedTrail(), fog: phase === 'play', ...extra });
  }

  function setReachFor(s: Ship): void {
    const reach = reachable(g, s, false, visibleOnBoard);
    reachSet = new Set(Object.keys(reach));
    reachList = Object.values(reach).map((o) => [o.c, o.r, !o.stop] as [number, number, boolean]);
  }

  // ---- undo / redo (this turn's moves) --------------------------------------
  // Each undoable action (a move, anchor, submerge) snapshots the whole game state
  // + selection + log BEFORE it happens; undo restores the previous snapshot and
  // pushes the current onto the redo stack. History is per movement phase — it's
  // cleared when the turn ends (the enemy move + combat are not undoable).
  interface Snapshot { state: string; selectedId: number | null; log: LogEntry[]; }
  const undoStack: Snapshot[] = [];
  const redoStack: Snapshot[] = [];
  function snapshot(): Snapshot {
    return { state: JSON.stringify(g), selectedId, log: [...log] };
  }
  /** Call right BEFORE a move/anchor/submerge mutates state. */
  function recordHistory(): void {
    undoStack.push(snapshot());
    if (undoStack.length > 60) undoStack.shift();
    redoStack.length = 0;
    refreshHistoryButtons();
  }
  function clearHistory(): void {
    undoStack.length = 0;
    redoStack.length = 0;
    refreshHistoryButtons();
  }
  function applySnapshot(s: Snapshot): void {
    // Mutate g IN PLACE (the renderer + closures hold this exact reference) so the
    // restored state is what everything reads on the next redraw.
    const snap = JSON.parse(s.state) as GameState;
    const gr = g as unknown as Record<string, unknown>;
    for (const k of Object.keys(gr)) delete gr[k];
    Object.assign(g, snap);
    selectedId = s.selectedId;
    log.length = 0;
    log.push(...s.log);
    reselectLastMove(); // after an undo the newly-last move becomes the selected trail
    const sh = sel();
    if (sh) setReachFor(sh); else { reachSet = new Set(); reachList = []; }
    refreshBoard();
    refreshHud();
    renderLog();
    autosave();
  }
  function undo(): void {
    if (phase !== 'play' || g.winner || cineActive || ordersMode || !undoStack.length) return;
    redoStack.push(snapshot());
    applySnapshot(undoStack.pop()!);
    advisory('Move undone.');
  }
  function redo(): void {
    if (phase !== 'play' || g.winner || cineActive || ordersMode || !redoStack.length) return;
    undoStack.push(snapshot());
    applySnapshot(redoStack.pop()!);
    advisory('Move redone.');
  }
  function refreshHistoryButtons(): void {
    const canEdit = phase === 'play' && !g.winner && !cineActive && !ordersMode;
    ($('ar-undo') as HTMLButtonElement).disabled = !canEdit || !undoStack.length;
    ($('ar-redo') as HTMLButtonElement).disabled = !canEdit || !redoStack.length;
  }

  function select(s: Ship): void {
    selectedId = s.id;
    setReachFor(s);
    refreshBoard();
    advisory(`Selected ${UNITS[s.type].name}. Click a buoy to move, or F to end turn.`);
    refreshHud();
    tutEvent(`select:${s.type}` as TutorialTrigger);
  }

  function deselect(): void {
    selectedId = null;
    reachSet = new Set();
    reachList = [];
    refreshBoard();
    refreshHud();
  }

  // ---- ship actions (anchor / submerge) -------------------------------------
  // Shared by the D/U keys and the on-screen #ar-anchor / #ar-submerge buttons,
  // so the game is fully playable by mouse, touch, or keyboard.
  function doAnchor(s: Ship): void {
    recordHistory();
    if (!toggleAnchored(s)) { undoStack.pop(); refreshHistoryButtons(); return; } // no-op → drop the snapshot
    setReachFor(s); // anchored → no reachable tiles; released → its moves return
    refreshBoard();
    refreshHud();
    autosave();
  }
  function doSubmerge(s: Ship): void {
    recordHistory();
    if (!toggleSubmerged(s)) { undoStack.pop(); refreshHistoryButtons(); return; } // no-op → drop the snapshot
    setReachFor(s);
    refreshBoard();
    refreshHud();
    autosave();
  }

  /** Sync the on-screen action buttons to the current selection. Every action has
   *  a button; they stay put and just disable when they don't apply (no
   *  selection, wrong ship type, already moved/done, or not in the play phase). */
  function refreshControls(): void {
    const s = phase === 'play' && !g.winner ? sel() : undefined;
    const anchorBtn = $('ar-anchor') as HTMLButtonElement;
    const subBtn = $('ar-submerge') as HTMLButtonElement;
    anchorBtn.disabled = !s || (s.done && !s.anchored); // anchored → enabled so it can be released
    // The .ico span carries the SVG glyph; the .lbl span (hidden in the icon-only
    // narrow layout) carries the word. The ⚓ icon is the same anchored or not.
    setLbl(anchorBtn, s?.anchored ? 'Anchored' : 'Anchor');
    const sub = s?.type === 'SS' ? s : undefined;
    subBtn.disabled = !sub || sub.moved > 0 || sub.done;
    (subBtn.querySelector('.ico') as HTMLElement).innerHTML = sub?.submerged ? ICON_SURFACE : ICON_DIVE;
    setLbl(subBtn, sub?.submerged ? 'Surface' : 'Submerge');
    // Unselect: enabled only while a ship is selected (replaces the old Orders
    // button — orders now open from the "Fleet" tab at the top-left of the board).
    const unselBtn = $('ar-unselect') as HTMLButtonElement;
    unselBtn.disabled = !s;
    // Hover tooltips describing each action — only while the labels are hidden
    // (icon-only layout); when the words show, a tooltip would be redundant.
    const tip = iconOnly.matches;
    setTip(anchorBtn, tip && (s?.anchored ? 'Release anchor (D)' : 'Anchor (D)'));
    setTip(subBtn, tip && (sub?.submerged ? 'Surface (U)' : 'Submerge (U)'));
    setTip(unselBtn, tip && 'Unselect (Esc)');
    setTip($('ar-end'), tip && 'End turn');
    refreshHistoryButtons();
    refreshTabs();
  }
  const setLbl = (btn: HTMLElement, text: string): void => {
    (btn.querySelector('.lbl') as HTMLElement).textContent = text;
  };
  const setTip = (btn: HTMLElement, text: string | false): void => {
    if (text) btn.title = text; else btn.removeAttribute('title');
  };
  // labels hide / tooltips show below this size (mirrors the icon-only @media rule)
  const iconOnly = window.matchMedia('(max-width: 440px) and (max-height: 1000px), (max-height: 600px)');
  iconOnly.addEventListener('change', refreshControls);

  function clickHex(c: number, r: number): void {
    if (phase !== 'play' || g.winner || cineActive) return;
    if (ordersMode) { handleOrdersClick(c, r); return; }
    const s = sel();
    // Move only to a legal STOP (an empty tile). Grey buoys mark friendly tiles a
    // ship may pass through but not stop on — clicking one falls through to the
    // select-ship logic below instead of attempting an illegal move.
    if (s && reachSet.has(`${c},${r}`) && canStop(g, s, c, r)) {
      const path = pathTo(g, s, c, r, false, visibleOnBoard);
      const from: [number, number] = [s.c, s.r];
      let walked: [number, number][] = [];
      if (path && path.length) {
        recordHistory(); // snapshot before the move so it can be undone
        walked = executeMove(g, s, path); // the tiles ACTUALLY traversed (may stop short)
        if (walked.length) {
          // record the traversed path (from → resting tile) so its dotted trail can
          // be re-shown by clicking this move in the log; newest move auto-selects.
          pushMove(`<span class="${human}">${UNITS[s.type].name}</span> → ${s.c},${s.r}`, [from, ...walked]);
          tutEvent('move');
        } else {
          undoStack.pop(); // ship couldn't advance (e.g. a hidden sub on the first tile) — drop the snapshot
          refreshHistoryButtons();
        }
      }
      // slide the ship along the path it truly walked, settling when it lands.
      const settle = (): void => {
        refreshBoard();
        refreshHud();
      };
      if (walked.length) view.animateMove(s.id, [from, ...walked], settle);
      else settle();
      // Unselect as soon as the ship is sent (Scott) — don't wait for the slide.
      // animateMove has already hidden the moving ship, so deselect's redraw keeps
      // it hidden; the ring + buoys clear at once. The dotted trail stays (it's
      // driven by the move log, not the selection). Reselect to move it further.
      deselect();
      autosave();
      return;
    }
    const mineHere = shipsAt(g, c, r).filter((x) => x.side === human);
    if (mineHere.length) {
      const next = mineHere.find((x) => x.id !== selectedId) ?? mineHere[0]!;
      select(next);
      return;
    }
    deselect();
  }

  /** Open the shipyard Build window (Build tab, top-left beside Fleet). Buying is an
   *  instant points purchase, so this can be done any time during your movement
   *  phase. Same window chrome as the Fleet window, and mutually exclusive with it
   *  (they share the top-left slot). */
  function openBuild(): void {
    if (buildOpen || phase !== 'play' || g.winner || cineActive) return;
    if (ordersMode) closeOrders(); // one window in the top-left slot at a time
    audio.playClick();
    buildOpen = true;
    const panel = document.createElement('div');
    panel.className = 'ar-owin';
    panel.id = 'ar-build-panel';
    panel.innerHTML = `<div class="hd"><button class="cls" id="ar-build-x" title="Close">${ICON_X}<span>Close</span></button>`
      + `<h3>${ICON_HAMMER}Build</h3></div><div class="body" id="ar-build-body"></div>`;
    boardEl.appendChild(panel);
    panel.querySelector('#ar-build-x')?.addEventListener('click', closeBuild);
    renderBuildYards(panel.querySelector<HTMLElement>('#ar-build-body')!, g, human, (msg) => {
      pushBuild(`<span class="R">${ICON_HAMMER}</span> ${msg}`);
      refreshBoard(); // the new ship appears on the board
      refreshHud();
      autosave();
    });
    refreshControls();
  }
  function closeBuild(): void {
    if (!buildOpen) return;
    buildOpen = false;
    boardEl.querySelector('#ar-build-panel')?.remove();
    refreshControls();
  }
  function toggleBuild(): void {
    if (buildOpen) closeBuild(); else openBuild();
  }

  /** End Turn asks for confirmation first, listing this turn's orders (building is
   *  its own Build button now). Confirm runs doEndTurn; cancel returns to planning. */
  function endTurn(): void {
    if (phase !== 'play' || g.winner || cineActive || confirmOpen) return;
    if (ordersMode) closeOrders(); // don't leave the orders list open across the turn
    audio.playClick();
    showEndTurnConfirm();
  }

  /** Modal listing the moves + builds made this turn, with Confirm / Keep-planning.
   *  Moves and builds tag their log entries with the turn, so we just filter the log. */
  function showEndTurnConfirm(): void {
    confirmOpen = true;
    const orders = log.filter((e) => e.move?.turn === g.turn || e.build?.turn === g.turn);
    const body = orders.length
      ? `<div class="ar-confirm-list">${orders.map((o) => `<span class="chip">${o.html}</span>`).join('')}</div>`
      : '<div class="ar-confirm-none">No moves or builds this turn.</div>';
    const over = document.createElement('div');
    over.className = 'ar-confirm';
    over.id = 'ar-confirm';
    over.innerHTML = `<div class="ar-confirm-card">`
      + `<h2>End turn ${g.turn}?</h2>`
      + `<div class="ar-confirm-sub">The enemy fleet will move and combat resolves — this can't be undone.</div>`
      + body
      + `<div class="ar-confirm-btns">`
      + `<button class="ar-btn" id="ar-confirm-cancel">Keep planning</button>`
      + `<button class="ar-btn primary" id="ar-confirm-ok">End turn ${ICON_NEXT}</button>`
      + `</div></div>`;
    wrap.appendChild(over);
    const close = (): void => {
      confirmOpen = false;
      over.remove();
      document.removeEventListener('keydown', onKey, true);
    };
    const confirm = (): void => { close(); doEndTurn(); };
    function onKey(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); close(); }
      else if (ev.key === 'Enter') { ev.preventDefault(); ev.stopPropagation(); confirm(); }
    }
    over.querySelector('#ar-confirm-cancel')?.addEventListener('click', close);
    over.querySelector('#ar-confirm-ok')?.addEventListener('click', confirm);
    over.addEventListener('click', (ev) => { if (ev.target === over) close(); }); // click backdrop to dismiss
    document.addEventListener('keydown', onKey, true);
  }

  function doEndTurn(): void {
    deselect();
    clearHistory(); // the enemy's move + combat aren't undoable — start each turn fresh
    tutEvent('endmove');
    aiMoveAll(g, aiSide, aiProfile); // the enemy admiral moves (mutates positions)
    // "End of turn, both sides": both fleets have now moved, so combat resolves
    // once — every adjacent enemy pair fights a single mutual round. resolveCombatSites
    // is pure (a pre-combat snapshot), so we can compute it now and still redraw the
    // board in its post-move / pre-combat state below.
    const sites = resolveCombatSites(g);

    // Everything from combat resolution onward, deferred so the player first SEES
    // where the enemy fleet moved (see the reveal beat below).
    const runCombat = (): void => {
      const rep = endOfTurn(g); // advances g.turn — this turn's moves are now history
      reselectLastMove(); // → none: last turn's move trails stop being selectable/drawn
      reportCombat(sites.flatMap((s) => s.events));
      for (const cap of rep.captures) {
        const city = g.cities.find((ct) => ct.name === cap.name);
        if (cap.neutralized) {
          pushLog(`${cap.name} neutralized — no ${ICON_VP}`);
          if (city) floatPopup(city.c, city.r, `${cap.name} neutral`, 'x');
        } else if (cap.changedHands && cap.owner) {
          pushLog(`${teamChip(cap.owner)} took ${cap.name}${cap.doubled ? ' (home — ×2!)' : ''}`);
          if (city) floatPopup(city.c, city.r, `+${ICON_VP}${cap.doubled ? city.vp * 2 : city.vp}${cap.doubled ? ' ×2' : ''}`, cap.owner);
        }
      }
      refreshBoard();
      refreshHud();
      autosave();
      if (!g.winner) tutEvent('newturn'); // endOfTurn advanced the turn counter

      // The board state has already advanced; the cinematic is a deterministic
      // replay of this turn's combat. Game-over / next-turn prompts wait for it.
      const finish = (): void => {
        if (g.winner) showGameOver();
        else advisory(`Turn ${g.turn} of ${g.maxTurn}. ${nextTip()}`);
      };
      if (sites.length && !getSkipCinematic()) playCinematic(sites, finish);
      else {
        // No cinematic (skipped, or nothing fought): the reveal beat below may have
        // locked input and disabled End Turn, so always hand control back here —
        // mirror playCinematic's onDone. (Harmless when the reveal didn't run.)
        cineActive = false;
        if (!g.winner) ($('ar-end') as HTMLButtonElement).disabled = false;
        refreshControls();
        refreshTabs();
        finish();
      }
    };

    // Reveal the enemy's completed move BEFORE combat resolves. Previously the AI
    // moved and combat fired in the same tick, so an enemy that slipped through
    // clear water right beside you was never drawn — you'd "suddenly find yourself
    // in battle". Now, when a clash is coming, redraw the post-move board and hold a
    // short, input-locked beat so the fleet is visible in position first.
    refreshBoard();
    if (sites.length) {
      cineActive = true; // lock input during the reveal (playCinematic keeps it set)
      ($('ar-end') as HTMLButtonElement).disabled = true;
      refreshControls();
      refreshTabs();
      advisory('The enemy fleet advances…');
      window.setTimeout(runCombat, ENEMY_MOVE_REVEAL_MS);
    } else {
      runCombat(); // nobody's fighting — no need to pause
    }
  }

  /** Mount the WP-8 combat viewer over the board; blocks input until dismissed. */
  function playCinematic(sites: CombatSite[], onDone: () => void): void {
    cineActive = true;
    ($('ar-end') as HTMLButtonElement).disabled = true;
    refreshControls(); // disable actions behind the cinematic
    refreshTabs(); // hide the Fleet/Build tabs behind the cinematic
    advisory('Combat! Watch the battle unfold…');
    void startCombatViewer({
      // Full-screen takeover (per shot.mjs — "a full HTML overlay"): mount over the
      // whole game chrome, not just the board, so the top scoreboard and the bottom
      // move-log bar don't peek out around a half-screen battle.
      container: wrap,
      sites,
      viewSide: human,
      seed: (g.seed ^ (g.turn * 0x9e3779b1)) >>> 0,
      onDone: () => {
        cineActive = false;
        if (!g.winner) ($('ar-end') as HTMLButtonElement).disabled = false;
        refreshControls(); // re-enable actions for the new turn
        refreshTabs();
        onDone();
      },
    });
  }

  function reportCombat(events: CombatEvent[]): void {
    let sunk = 0;
    for (const e of events) if (e.sunk) sunk++;
    if (events.length) {
      pushLog(`${ICON_SWORDS} ${events.length} shots fired, ${sunk} ship${sunk === 1 ? '' : 's'} sunk`);
    }
  }

  function showGameOver(): void {
    clearSave();
    tut?.destroy();
    audio.stopMusic();
    const youWon = g.winner === human;
    if (youWon) audio.playFanfare();
    else audio.playSink();
    const p = vpProjection(g);
    const shipsR = g.ships.filter((s) => s.side === 'R').length;
    const shipsY = g.ships.filter((s) => s.side === 'Y').length;
    const citiesR = g.cities.filter((c) => c.owner === 'R').length;
    const citiesY = g.cities.filter((c) => c.owner === 'Y').length;
    const cushion =
      p.overWin >= 0
        ? `Over-win: ${p.overWin}% beyond the required ${p.ratio}%.`
        : `Fell ${-p.overWin}% short of the required ${p.ratio}%.`;

    // Campaign result line (records sway/renown + persists) and tutorial framing.
    const campMsg = mode === 'campaign' && opts.onResult ? opts.onResult(youWon, g) : '';
    const heading = mode === 'tutorial'
      ? 'TUTORIAL COMPLETE'
      : youWon ? 'VICTORY' : 'DEFEAT';
    const headClass = mode === 'tutorial' ? 'win' : youWon ? 'win' : 'lose';
    // team side shown by its chip (colour + O/X shape), never the words
    // "Red"/"Yellow" — the game's no-names standard (matches the scoreboard).
    const chip = teamChip;
    const sub = mode === 'tutorial'
      ? `You ${youWon ? 'won' : 'fought to the end'} — ${chip('R')} ${ICON_VP}${g.vp.R} vs ${chip('Y')} ${ICON_VP}${g.vp.Y}. You know the ropes now, Admiral.`
      : `${chip(g.winner === 'R' ? 'R' : 'Y')} wins after ${g.maxTurn} turns. ${cushion}`;

    // mode-specific footer buttons (campaign returns to the map; others navigate)
    let buttons: string;
    if (mode === 'campaign') {
      buttons = '<button class="ar-btn primary" id="ar-camp-return" style="width:240px">◂ Back to campaign map</button>';
    } else if (mode === 'tutorial') {
      buttons = '<button class="ar-btn primary" id="ar-tut-quick" style="width:200px">Quick Battle ▶</button>'
        + `<button class="ar-btn" id="ar-tut-camp" style="width:200px;margin-top:8px">Campaign <span class="ic">${ICON_ANCHOR}</span></button>`;
    } else if (mode === 'instant') {
      buttons = '<button class="ar-btn primary" id="ar-inst-again" style="width:240px">◂ Choose another instant battle</button>';
    } else {
      buttons = '<button class="ar-btn primary" id="ar-again" style="width:200px" onclick="location.reload()">New battle</button>';
    }

    const over = document.createElement('div');
    over.className = 'ar-over';
    over.id = 'ar-over';
    over.innerHTML = `
      <h1 class="${headClass}">${heading}</h1>
      <div class="sub">${sub}</div>
      <table>
        <tr><td class="l"></td><td class="R hd">${chip('R')}</td><td class="Y hd">${chip('Y')}</td></tr>
        <tr><td class="l">Points</td><td class="R">${ICON_VP}${g.vp.R}</td><td class="Y">${ICON_VP}${g.vp.Y}</td></tr>
        <tr><td class="l">Cities held</td><td class="R">${citiesR}</td><td class="Y">${citiesY}</td></tr>
        <tr><td class="l">Ships surviving</td><td class="R">${shipsR}</td><td class="Y">${shipsY}</td></tr>
        <tr><td class="l">Final margin</td><td class="R">${p.marginPct}%</td><td class="Y">need ${p.ratio}%</td></tr>
      </table>
      ${campMsg ? `<div class="sub" style="max-width:520px">${campMsg}</div>` : ''}
      ${buttons}`;
    boardEl.appendChild(over);
    advisory(youWon ? 'You win the battle!' : 'The enemy prevailed.');
    ($('ar-end') as HTMLButtonElement).disabled = true;

    over.querySelector('#ar-camp-return')?.addEventListener('click', () => opts.onExit?.());
    over.querySelector('#ar-tut-quick')?.addEventListener('click', () => {
      location.search = '?scenario=play&fresh=1';
    });
    over.querySelector('#ar-tut-camp')?.addEventListener('click', () => {
      location.search = '?scenario=campaign';
    });
    over.querySelector('#ar-inst-again')?.addEventListener('click', () => opts.onExit?.());
  }

  // ---- standing orders (destinations) — a planning aid, not auto-movement -----
  // A ship may carry a "course" (Ship.dest): a dotted trail to a port so you can
  // "follow the dots" when moving it by hand (the rules never move it for you).
  // Courses are set on the buy screen and managed here during play via the Orders
  // panel — the manual's "L" ship list. The Orders button enters a light "orders
  // mode" where a board click sets a course instead of moving, so the two gestures
  // never collide: click one of your ships, then click a port.

  /** One row of the ship-orders list: the ship name + a destination picker, laid
   *  out as an aligned table row like the buy screen (no +/× toggle — the dropdown
   *  is always shown). Selecting a row, or tapping a port on the map, targets that
   *  ship. Any port is a valid course; the option value is the g.cities index. */
  function orderRow(s: Ship): string {
    const on = s.id === selectedId ? ' on' : '';
    const destOpts = `<option value="-1"${s.dest ? '' : ' selected'}>— none —</option>`
      + g.cities
          .map((c, ci) => {
            const isDest = s.dest && s.dest[0] === c.c && s.dest[1] === c.r;
            return `<option value="${ci}"${isDest ? ' selected' : ''}>${c.name}</option>`;
          })
          .join('');
    return `<div class="tf${on}" data-id="${s.id}"><span class="nm">${UNITS[s.type].name}</span>`
      + `<select class="to" data-id="${s.id}">${destOpts}</select></div>`;
  }

  /** (Re)build the contents of the open orders panel from current ship state. */
  function renderOrdersPanel(): void {
    const panel = boardEl.querySelector<HTMLElement>('#ar-orders-panel');
    if (!panel) return;
    const ships = g.ships.filter((s) => s.side === human);
    const rows = ships.length
      ? ships.map(orderRow).join('')
      : '<div class="none">You have no ships, Admiral.</div>';
    panel.innerHTML = `<div class="hd"><button class="cls" id="ar-orders-x" title="Close">${ICON_X}<span>Close</span></button>`
      + `<h3>${ICON_FLAG}Fleet</h3></div>`
      + `<div class="body"><p class="hint">Set a course from the menu, or tap a ship then a port on the map.</p>${rows}</div>`;
    panel.querySelector('#ar-orders-x')?.addEventListener('click', closeOrders);
    // destination picker: set or clear the course.
    panel.querySelectorAll<HTMLSelectElement>('select.to').forEach((selEl) => {
      selEl.addEventListener('change', () => {
        const s = g.ships.find((x) => x.id === Number(selEl.dataset['id']));
        if (!s) return;
        const v = Number(selEl.value);
        const city = v < 0 ? null : g.cities[v];
        if (city) { s.dest = [city.c, city.r]; advisory(`${UNITS[s.type].name} ordered to ${city.name}.`); }
        else { delete s.dest; advisory(`${UNITS[s.type].name} course cleared.`); }
        refreshBoard();
        renderOrdersPanel();
        refreshHud();
        autosave();
      });
    });
    panel.querySelectorAll<HTMLElement>('.tf').forEach((row) => {
      row.addEventListener('click', (ev) => {
        if ((ev.target as HTMLElement).closest('select, button')) return; // controls handle themselves
        const s = g.ships.find((x) => x.id === Number(row.dataset['id']));
        if (s) selectForOrders(s);
      });
    });
  }

  /** Select a ship for ordering (no move buoys — we're not moving it). */
  function selectForOrders(s: Ship): void {
    selectedId = s.id;
    reachSet = new Set();
    reachList = [];
    refreshBoard();
    refreshHud();
    renderOrdersPanel();
    advisory(`${UNITS[s.type].name} selected — click a port to set its course.`);
  }

  /** Set (or, if clicking the current course, clear) the selected ship's course. */
  function setCourseTo(c: number, r: number): void {
    const s = sel();
    if (!s || s.side !== human) return;
    const city = cityAt(g, c, r);
    if (!city) return; // only a port can be a destination
    if (s.dest && s.dest[0] === c && s.dest[1] === r) {
      delete s.dest;
      advisory(`${UNITS[s.type].name} course cleared.`);
    } else {
      s.dest = [c, r];
      advisory(`${UNITS[s.type].name} ordered to ${city.name}.`);
    }
    refreshBoard();
    renderOrdersPanel();
    refreshHud();
    autosave();
  }

  /** Board click while in orders mode: pick one of your ships, or (with a ship
   *  selected) click a port to set its course. */
  function handleOrdersClick(c: number, r: number): void {
    const mine = shipsAt(g, c, r).filter((x) => x.side === human);
    if (mine.length) {
      const next = mine.find((x) => x.id !== selectedId) ?? mine[0]!;
      selectForOrders(next);
      return;
    }
    if (sel()) setCourseTo(c, r);
  }

  function openOrders(): void {
    if (ordersMode || phase !== 'play' || g.winner || cineActive) return;
    if (buildOpen) closeBuild(); // one window in the top-left slot at a time
    ordersMode = true;
    deselect(); // start clean; order trails stay drawn via refreshBoard
    const panel = document.createElement('div');
    panel.className = 'ar-owin';
    panel.id = 'ar-orders-panel';
    boardEl.appendChild(panel);
    renderOrdersPanel();
    advisory('Orders — click one of your ships, then a port to set its course.');
    refreshControls();
  }
  function closeOrders(): void {
    if (!ordersMode) return;
    ordersMode = false;
    boardEl.querySelector('#ar-orders-panel')?.remove();
    deselect();
    refreshControls();
  }
  function toggleOrders(): void {
    if (ordersMode) closeOrders(); else openOrders();
  }

  // The top-left tabs — "Fleet" (ship orders) and "Build" (shipyard) — open their
  // respective floating windows (same tab treatment as the buy screen). They sit
  // side by side in a flex container and hide while either window is open (the
  // windows share the top-left slot). Replaces the old bottom-bar Orders/Build
  // buttons.
  const playTabs = document.createElement('div');
  playTabs.className = 'ar-owin-tabs';
  playTabs.style.display = 'none';
  const ordersTab = document.createElement('button');
  ordersTab.className = 'ar-owin-reopen';
  ordersTab.id = 'ar-orders-reopen';
  ordersTab.innerHTML = `${ICON_FLAG}<span>Fleet</span>`;
  ordersTab.addEventListener('click', openOrders);
  const buildTab = document.createElement('button');
  buildTab.className = 'ar-owin-reopen';
  buildTab.id = 'ar-build-reopen';
  buildTab.innerHTML = `${ICON_HAMMER}<span>Build</span>`;
  buildTab.addEventListener('click', openBuild);
  playTabs.append(ordersTab, buildTab);
  boardEl.appendChild(playTabs);
  function refreshTabs(): void {
    const base = phase === 'play' && !g.winner && !cineActive && mode !== 'tutorial';
    playTabs.style.display = base && !ordersMode && !buildOpen ? '' : 'none';
    // Build stays visible even with no shipyard yet — the window explains how to get
    // one (renderBuildYards' empty state), same as the always-present Fleet tab.
  }

  // ---- map zoom (bottom-right +/− buttons + keys) ----
  function applyZoom(): void {
    zoomLevel = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoomLevel));
    view.zoom(zoomLevel);
    view.renderOnce();
    const zin = wrap.querySelector<HTMLButtonElement>('#ar-zoom-in');
    const zout = wrap.querySelector<HTMLButtonElement>('#ar-zoom-out');
    if (zin) zin.disabled = zoomLevel >= ZOOM_MAX - 1e-6;
    if (zout) zout.disabled = zoomLevel <= ZOOM_MIN + 1e-6;
    const lvl = wrap.querySelector<HTMLElement>('#ar-zoom-lvl');
    if (lvl) lvl.textContent = `${zoomLevel.toFixed(1)}×`;
  }
  function zoomBy(delta: number): void {
    zoomLevel += delta;
    applyZoom();
  }
  /** Build the +/− control stacked just above the minimap (bottom-right). */
  function createZoomControls(): void {
    if (boardEl.querySelector('.ar-zoom')) return;
    const miniH = minimapSize(g, 180).h; // mirrors render/board.ts minimap
    const zc = document.createElement('div');
    zc.className = 'ar-zoom';
    // Bottom of the column depends on whether the minimap is showing: sit 6px above
    // the minimap's silver frame (10 pad + miniH + 3 frame border + 6 gap), or drop
    // to the 10px page padding when it's hidden so the controls fall to the corner.
    const bottomWithMini = 10 + miniH + 3 + 6;
    const bottomNoMini = 10;
    zc.style.bottom = `${bottomWithMini}px`;
    zc.innerHTML = `
      <button id="ar-zoom-in" title="Zoom in (+)">${ZOOM_PLUS}</button>
      <button id="ar-zoom-out" title="Zoom out (−)">${ZOOM_MINUS}</button>
      <div class="ar-zoom-row">
        <button id="ar-mini-toggle" class="ar-mini-toggle" title="Hide minimap">${MINIMAP_ICON}</button>
        <button id="ar-zoom-lvl" class="ar-zoom-lvl" title="Cycle zoom — 1× / 2× / 3×"></button>
      </div>`;
    boardEl.appendChild(zc);
    // Minimap show/hide toggle. The glyph swaps (framed map ⇄ struck-through) like a
    // mute button, and the whole control column drops down to the corner when off, so
    // the +/−/level buttons move into the freed minimap footprint.
    let minimapShown = true;
    const miniBtn = zc.querySelector<HTMLButtonElement>('#ar-mini-toggle')!;
    miniBtn.addEventListener('click', () => {
      minimapShown = !minimapShown;
      view.setMinimapShown(minimapShown);
      zc.style.bottom = `${minimapShown ? bottomWithMini : bottomNoMini}px`;
      miniBtn.classList.toggle('is-off', !minimapShown);
      miniBtn.title = minimapShown ? 'Hide minimap' : 'Show minimap';
    });
    // The zoom read-out doubles as a button: cycle 1× → 2× → 3× → 1×. A non-whole
    // level (e.g. fine-stepped to 1.3× with the +/− keys) snaps back to 1× first.
    zc.querySelector<HTMLButtonElement>('#ar-zoom-lvl')!.addEventListener('click', () => {
      const e = 1e-6;
      if (Math.abs(zoomLevel - 1) < e) zoomLevel = 2;
      else if (Math.abs(zoomLevel - 2) < e) zoomLevel = 3;
      else if (Math.abs(zoomLevel - 3) < e) zoomLevel = 1;
      else zoomLevel = 1; // not a round level → restart the cycle from 1×
      applyZoom();
    });
    // A plain click = exactly one step. Only after holding past HOLD_DELAY does
    // it start repeating every 90ms, self-stopping when clamped at the limit.
    const HOLD_DELAY = 350;
    const holdZoom = (sel: string, delta: number): void => {
      const btn = zc.querySelector<HTMLButtonElement>(sel)!;
      let delayTimer = 0;
      let repeatTimer = 0;
      const stop = (): void => {
        if (delayTimer) { clearTimeout(delayTimer); delayTimer = 0; }
        if (repeatTimer) { clearInterval(repeatTimer); repeatTimer = 0; }
      };
      const atLimit = (): boolean =>
        delta > 0 ? zoomLevel >= ZOOM_MAX - 1e-6 : zoomLevel <= ZOOM_MIN + 1e-6;
      btn.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        zoomBy(delta); // single step on press
        stop();
        delayTimer = window.setTimeout(() => {
          repeatTimer = window.setInterval(() => { if (atLimit()) stop(); else zoomBy(delta); }, 90);
        }, HOLD_DELAY);
      });
      for (const e of ['pointerup', 'pointerleave', 'pointercancel'] as const) {
        btn.addEventListener(e, stop);
      }
    };
    holdZoom('#ar-zoom-in', ZOOM_STEP);
    holdZoom('#ar-zoom-out', -ZOOM_STEP);
    applyZoom(); // initial render + disabled states (− disabled at min zoom)
  }

  // ---- input wiring (play phase only) ----
  function wirePlayInput(): void {
    view.renderer.domElement.addEventListener('click', (ev) => {
      const hex = view.screenToHex(ev.clientX, ev.clientY);
      if (hex) clickHex(hex[0], hex[1]);
    });
    createZoomControls();
    ($('ar-end') as HTMLButtonElement).disabled = false;
  }
  $('ar-end').addEventListener('click', endTurn);
  $('ar-anchor').addEventListener('click', () => { const s = sel(); if (s) doAnchor(s); });
  $('ar-submerge').addEventListener('click', () => { const s = sel(); if (s) doSubmerge(s); });
  $('ar-undo').addEventListener('click', undo);
  $('ar-redo').addEventListener('click', redo);
  // Unselect clears the current selection (clicking open sea still works too).
  $('ar-unselect').addEventListener('click', deselect);

  // ---- settings overlay (gear button) ----
  // Mute now lives in the settings panel (volume + mute); the M key still toggles it.
  let settingsHandle: SettingsHandle | null = null;
  function closeSettings(): void {
    settingsHandle?.destroy();
    settingsHandle = null;
  }
  function openSettings(): void {
    if (settingsHandle) return;
    settingsHandle = showSettings({
      container: wrap,
      overlay: true,
      backLabel: 'Close',
      onBack: closeSettings,
      onReturnToMenu: () => { closeSettings(); location.search = '?scenario=title'; },
    });
  }
  $('ar-settings-btn').addEventListener('click', openSettings);
  const kickAudio = (): void => {
    audio.resume();
    audio.startMusic();
    window.removeEventListener('pointerdown', kickAudio);
  };
  window.addEventListener('pointerdown', kickAudio);

  window.addEventListener('keydown', (ev) => {
    // While the end-turn confirmation is up it owns the keyboard (its own capture
    // listener handles Enter/Esc); swallow everything else so nothing fires behind it.
    if (confirmOpen) return;
    // Escape closes the settings overlay, then orders mode, then a selection.
    if (ev.key === 'Escape' && settingsHandle) { closeSettings(); return; }
    if (ev.key === 'Escape' && ordersMode) { closeOrders(); return; }
    if (ev.key === 'Escape' && selectedId != null) { deselect(); return; }
    // Undo / redo the last move (Ctrl/⌘+Z, Ctrl+Y or Ctrl+Shift+Z).
    if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'z' || ev.key === 'Z')) {
      ev.preventDefault();
      if (ev.shiftKey) redo(); else undo();
      return;
    }
    if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'y' || ev.key === 'Y')) {
      ev.preventDefault();
      redo();
      return;
    }
    if (ev.key === 'm' || ev.key === 'M') {
      audio.toggleMute();
      return;
    }
    // zoom works in the buy phase too (the map + minimap are live there); only the
    // combat cinematic blocks it.
    if (ev.key === '+' || ev.key === '=') {
      if (!cineActive) zoomBy(ZOOM_STEP);
      return;
    }
    if (ev.key === '-' || ev.key === '_') {
      if (!cineActive) zoomBy(-ZOOM_STEP);
      return;
    }
    if (phase !== 'play' || g.winner || cineActive) return;
    const s = sel();
    if (ev.key === 'f' || ev.key === 'F' || ev.key === 'Enter') endTurn();
    else if (ev.key === 'b' || ev.key === 'B') toggleBuild();
    else if (ev.key === 'o' || ev.key === 'O') toggleOrders();
    else if (s && (ev.key === 'd' || ev.key === 'D')) doAnchor(s);
    else if (s && (ev.key === 'u' || ev.key === 'U')) doSubmerge(s);
  });

  function startPlay(): void {
    // Enemy buys to the now-final difficulty (quick mode only).
    if (needsAiBuy && g.ships.every((s) => s.side !== aiSide)) aiBuyFleet(g, aiSide, aiProfile);
    wrap.classList.remove('ar-buying'); // restore the play-phase bottom bar
    g.phase = 'move';
    phase = 'play';
    buyHandle = null;
    wirePlayInput();
    resizeBoard(); // the bottom bar just swapped (buy menu → log/controls)
    refreshBoard(); // show any course trails carried over from the buy screen
    refreshHud();
    autosave();
    advisory('Red moves first. Capture neutral cities with your transports!');
    if (g.winner) showGameOver();
  }

  // ---- boot into the right phase ----
  renderLog(); // show the "No moves made." placeholder until the first move
  if (restored) {
    phase = 'play';
    wirePlayInput();
    if (selectedId != null) {
      const s = sel();
      if (s) setReachFor(s);
    }
    refreshBoard();
    renderLog();
    refreshHud();
    advisory(`Resumed — turn ${g.turn} of ${g.maxTurn}.`);
    if (g.winner) showGameOver();
  } else if (mode === 'tutorial') {
    // Fleets are pre-placed — go straight to play and open the lesson panel.
    startPlay();
    advisory('Tutorial — follow the panel, Admiral.');
    tut = startTutorial({ container: boardEl, onSkip: () => {}, onComplete: () => {} });
  } else if (mode === 'instant') {
    // Instant battles drop you into a pre-built fight — no buy phase. (manual:
    // instant battles "cannot be projected — every turn must be fought".)
    startPlay();
    advisory('Instant battle — both fleets are deployed. Fight every turn!');
  } else {
    wrap.classList.add('ar-buying');
    const buy = startBuyScreen(boardEl, wrap, view, g, human);
    buyHandle = buy.handle;
    phase = 'buy';
    createZoomControls(); // zoom +/− buttons + level read-out, alongside the minimap
    refreshHud();
    if (mode === 'campaign') advisory('Buy your fleet for the state battle.');
    void buy.done.then(startPlay);
  }

  wirePan();
  resizeBoard();
  // Re-fit the board and re-pick the prediction wording (full ⇄ compact) when the
  // viewport changes — e.g. a phone rotating across the 700px breakpoint.
  window.addEventListener('resize', () => {
    resizeBoard();
    if (phase === 'play') refreshPrediction();
    refreshControls(); // re-sync icon-only hover tooltips across the breakpoint
  });

  view.renderOnce();

  return {
    g,
    view,
    get phase() {
      return phase;
    },
    mode,
    get buy() {
      return buyHandle;
    },
    get tutorial() {
      return tut;
    },
    endTurn,
    clickHex,
    get selectedId() {
      return selectedId;
    },
    selectFirstMovable() {
      for (const s of g.ships) {
        if (s.side !== human || s.done) continue;
        const reach = reachable(g, s, false, visibleOnBoard);
        if (Object.keys(reach).length) {
          select(s);
          return { id: s.id, c: s.c, r: s.r };
        }
      }
      return null;
    },
    firstReachStop() {
      const tile = reachList.find(([, , canStop]) => canStop);
      return tile ? [tile[0], tile[1]] : null;
    },
  };
}
